// Extracted from CaseBuddy.vue in refactor pass 2.
// Date lookup, chronological sorting, raw import, Tool 68 preparation, path labels, and transaction-network lookup methods.
// These are Options API method definitions and intentionally use `this`.

import {
  abbreviateCaseBuddyHash,
  analyzeEntryUtcDateCompletion,
  analyzeNumericMainPathLabels,
  analyzeStartingMainTransactions,
  buildDateLookupAuditRows,
  buildDateSortAuditRows,
  buildMainDataWithPreparedSaplingReplacements,
  buildMainPathLabelAuditRows,
  buildMainRawDataCoverage,
  buildNetworkLookupAuditRows,
  buildRawDataAppendMatrix,
  buildRawDataImportAuditRows,
  buildWayFinderHandoffCaseId,
  buildWayFinderHandoffEndpoint,
  buildWayFinderHandoffJobPayload,
  buildWayFinderHandoffTargetsFromCoverage,
  CASE_WAYFINDER_HANDOFF_DEFAULT_BRIDGE_URL,
  CASE_WAYFINDER_HANDOFF_POLL_INTERVAL_MS,
  CASE_WAYFINDER_HANDOFF_SETTINGS_KEY,
  CASE_WAYFINDER_HANDOFF_STALE_MINUTES,
  mergeWayFinderHandoffJobsIntoTargets,
  normalizeWayFinderBridgeUrl,
  normalizeWayFinderHandoffJobs,
  summarizeWayFinderHandoffJobs,
  buildRawDataSetsFromMatrix,
  buildSaplingPreparationAnalysis,
  buildTool68InputRowsForRawSet,
  buildTool68PreparationAuditRows,
  buildTopLevelMainEntryDescriptors,
  CASE_BUDDY_NETWORK_LOOKUP_DELAY_MS,
  CASE_BUDDY_NETWORK_LOOKUP_ENDPOINT,
  CASE_COLUMN_HEADERS,
  CASE_DEFAULT_COLS,
  CASE_DEFAULT_ROWS,
  collectStoredAuthToken,
  extractAuthTokenCandidate,
  firstNonEmptyEntryValue,
  firstNonEmptyValue,
  formatNetworkLookupUtcDate,
  getFirstValidUtcDateFromEntryRows,
  normalizeAuthSession,
  normalizeCaseMatrix,
  normalizeCaseUtcDate,
  normalizeCell,
  normalizeComparisonKey,
  normalizeNumericMainPathLabel,
  normalizeRawDataImportRowsFromPaste,
  normalizeTool68ChainMode,
  normalizeTool68ProcessedRowsForMainRewrite,
  parseCaseEntries,
  parseCaseUtcDate,
  rowHasMeaningfulData,
  runTool68BFlowAnalysis,
  sleep,
  sortTopLevelMainEntriesForLabelPlan,
  WORKFLOW_SIGNAL_MAIN_ENTRIES_DATED_AND_ORDERED,
  WORKFLOW_SIGNAL_MAIN_ENTRIES_ORDERED_BY_DATE,
  WORKFLOW_SIGNAL_MAIN_TRANSACTION_PATH_LABELS_ASSIGNED,
  WORKFLOW_SIGNAL_RAW_DATA_LOADED,
  WORKFLOW_SIGNAL_TOOL_68_MAIN_DATA_PREPARED,
  WORKFLOW_SIGNAL_TRANSACTION_DATES_FILLED,
  WORKFLOW_SIGNAL_TRANSACTION_NETWORKS_IDENTIFIED
} from '../CaseBuddyCore.js';
import AuthService from '../../services/AuthService';

export const caseBuddyDateNetworkRawMethods = {
    buildMainTransactionDateLookupRows() {
      const mainData = this.getMatrixForLogicalSheet('main');
      const analysis = analyzeStartingMainTransactions(mainData);
      const entries = parseCaseEntries(mainData);
      const entryByStartRow = new Map(entries.map(entry => [entry.startRow, entry]));

      return analysis.mainTransactionRows.map(({ rowIndex, row }, index) => {
        const entry = entryByStartRow.get(rowIndex) || {
          startRow: rowIndex,
          hash: normalizeCell(row?.[1]),
          rows: [{ rowIndex, row }]
        };
        const entryRows = Array.isArray(entry.rows) && entry.rows.length > 0 ? entry.rows : [{ rowIndex, row }];
        const hash = normalizeCell(row?.[1] || entry.hash);
        const pathLabel = normalizeCell(row?.[0]);
        const chainId = firstNonEmptyEntryValue(entryRows, 19) || normalizeCell(row?.[19]);
        const chainName = firstNonEmptyEntryValue(entryRows, 20) || normalizeCell(row?.[20]);
        const toAddressHint = normalizeCell(row?.[3]) || firstNonEmptyEntryValue(entryRows, 3);
        const fromAddressHint = normalizeCell(row?.[2]) || firstNonEmptyEntryValue(entryRows, 2);
        const dateInfo = parseCaseUtcDate(row?.[4]);
        const entryDateInfo = analyzeEntryUtcDateCompletion(entryRows);
        const entryCanonicalDate = entryDateInfo.complete ? '' : (entryDateInfo.canonicalDate || getFirstValidUtcDateFromEntryRows(entryRows));
        const needsDate = !!hash && !entryDateInfo.complete;
        let status = 'complete';
        let statusLabel = 'Already filled';
        let message = 'This main transaction entry already has valid UTC dates on its populated rows.';

        if (needsDate) {
          if (entryDateInfo.hasConflict) {
            status = 'invalid';
            statusLabel = 'Date conflict';
            message = 'This main transaction entry has conflicting UTC dates; review it manually or use backend lookup to overwrite the entry date.';
          } else if (dateInfo.hasValue && !dateInfo.isValid) {
            status = entryCanonicalDate ? 'propagate' : 'invalid';
            statusLabel = entryCanonicalDate ? 'Can propagate' : 'Invalid date';
            message = entryCanonicalDate
              ? 'A valid date exists elsewhere in this main transaction entry and can be propagated.'
              : 'Column E has a value, but it could not be parsed as a UTC date.';
          } else if (entryCanonicalDate) {
            status = 'propagate';
            statusLabel = 'Can propagate';
            message = 'A valid date already exists elsewhere in this main transaction entry.';
          } else {
            status = 'pending';
            statusLabel = 'Needs lookup';
            message = 'Column E is blank or incomplete for this starting/main transaction entry.';
          }
        }

        return {
          id: `date-main-${rowIndex}-${index}-${normalizeComparisonKey(hash) || 'blank'}`,
          rowIndex,
          spreadsheetRowNumber: rowIndex + 1,
          entryRowIndexes: entryRows.map(entryRow => entryRow.rowIndex).filter(entryIndex => Number.isInteger(entryIndex) && entryIndex >= 0),
          pathLabel,
          hash,
          toAddressHint,
          fromAddressHint,
          chainId,
          chainName,
          currentDate: normalizeCell(row?.[4]),
          currentDateLabel: dateInfo.canonical || normalizeCell(row?.[4]) || 'Missing',
          entryCanonicalDate,
          dateToApply: '',
          needsDate,
          include: needsDate,
          status,
          statusLabel,
          message,
          source: entryCanonicalDate ? 'existing-entry-date' : '',
          tried: [],
          lookupResult: null
        };
      });
    },

    buildDateLookupSortPreviewState() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet) {
        return {
          totalBlocks: 0,
          datedBlocks: 0,
          undatedBlocks: 0,
          orderChanged: false,
          allDatesValid: false,
          chronologyPass: false,
          message: 'Main Data is not available yet.'
        };
      }

      const state = this.getSheetStateById(mainSheet.id);
      const data = normalizeCaseMatrix(state.data);
      const sortModel = this.buildMainEntryDateSortBlocks(data);
      const blocks = sortModel.blocks || [];
      const totalBlocks = blocks.length;
      const undatedBlocks = blocks.filter(block => !block.dateInfo?.isValid).length;
      const datedBlocks = totalBlocks - undatedBlocks;
      const sortedBlocks = blocks.slice().sort((left, right) => {
        const leftValid = !!left.dateInfo?.isValid;
        const rightValid = !!right.dateInfo?.isValid;
        if (leftValid && rightValid) {
          if (left.dateInfo.timestamp !== right.dateInfo.timestamp) {
            return left.dateInfo.timestamp - right.dateInfo.timestamp;
          }
          return left.originalOrder - right.originalOrder;
        }
        if (leftValid && !rightValid) return -1;
        if (!leftValid && rightValid) return 1;
        return left.originalOrder - right.originalOrder;
      });
      const orderChanged = sortedBlocks.some((block, index) => block.id !== blocks[index]?.id);
      const allDatesValid = totalBlocks > 0 && undatedBlocks === 0;
      const chronologyPass = allDatesValid && !orderChanged;
      let message = 'No starting/main transaction blocks were detected yet.';

      if (totalBlocks > 0 && chronologyPass) {
        message = 'UTC dates are filled and the main transaction blocks appear to be in chronological order. You can move on to path labels.';
      } else if (totalBlocks > 0 && !allDatesValid) {
        message = 'Finish missing UTC dates, then press Sort By Date before assigning path labels. Sorting now will move undated blocks after dated blocks.';
      } else if (totalBlocks > 0 && orderChanged) {
        message = 'The main transaction blocks are not in date order yet. Press Sort By Date before assigning path labels.';
      }

      return {
        totalBlocks,
        datedBlocks,
        undatedBlocks,
        orderChanged,
        allDatesValid,
        chronologyPass,
        message
      };
    },

    refreshDateLookupSortPreview() {
      this.dateLookupSortPreview = this.buildDateLookupSortPreviewState();
      return this.dateLookupSortPreview;
    },

    refreshDateLookupRows() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.dateLookupRows = this.buildMainTransactionDateLookupRows();
      this.refreshDateLookupSortPreview();
      this.dateLookupStatus = this.dateLookupStageSummary;
      return this.dateLookupRows;
    },

    selectPendingDateLookupRows() {
      this.dateLookupRows = this.dateLookupRows.map(row => ({
        ...row,
        include: !!row.needsDate && !!row.hash
      }));
    },

    clearDateLookupSelections() {
      this.dateLookupRows = this.dateLookupRows.map(row => ({
        ...row,
        include: false
      }));
    },

    updateDateLookupRow(rowId, patch = {}) {
      this.dateLookupRows = this.dateLookupRows.map(row => row.id === rowId ? { ...row, ...patch } : row);
    },

    lookupTransactionDate(row) {
      return this.postCaseBuddyApiJson(CASE_BUDDY_NETWORK_LOOKUP_ENDPOINT, {
        txHash: row.hash,
        toAddressHint: row.toAddressHint || '',
        fromAddressHint: row.fromAddressHint || '',
        fillDateIfMissing: true
      });
    },

    applyDateLookupResultsToMainSheet(rowsToApply = []) {
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet || !Array.isArray(rowsToApply) || rowsToApply.length === 0) {
        return { changed: false, entriesUpdated: 0, dateRowsFilled: 0, networkRowsFilled: 0, normalizedHashCount: 0 };
      }

      const state = this.getSheetStateById(mainSheet.id);
      const data = normalizeCaseMatrix(state.data);
      let changed = false;
      let entriesUpdated = 0;
      let dateRowsFilled = 0;
      let networkRowsFilled = 0;
      let normalizedHashCount = 0;

      rowsToApply.forEach((draft) => {
        const result = draft.lookupResult || {};
        const utcDate = normalizeCaseUtcDate(draft.dateToApply) || formatNetworkLookupUtcDate(result);
        if (!utcDate) return;

        const chainId = normalizeCell(result.caseChainId);
        const chainName = normalizeCell(result.caseChainName);
        const normalizedTxHash = normalizeCell(result.normalizedTxHash);
        const entryRowIndexes = Array.isArray(draft.entryRowIndexes) && draft.entryRowIndexes.length > 0
          ? draft.entryRowIndexes
          : [draft.rowIndex];
        let entryChanged = false;

        const maxRowIndex = Math.max(...entryRowIndexes, draft.rowIndex);
        while (data.length <= maxRowIndex) {
          data.push(Array(CASE_DEFAULT_COLS).fill(''));
        }

        entryRowIndexes.forEach((rowIndex) => {
          const row = Array.isArray(data[rowIndex]) ? data[rowIndex] : Array(CASE_DEFAULT_COLS).fill('');
          data[rowIndex] = row.length >= CASE_DEFAULT_COLS ? row : [...row, ...Array(CASE_DEFAULT_COLS - row.length).fill('')];

          if (rowHasMeaningfulData(data[rowIndex]) && normalizeCell(data[rowIndex][4]) !== utcDate) {
            data[rowIndex][4] = utcDate;
            changed = true;
            entryChanged = true;
            dateRowsFilled += 1;
          }

          if (this.dateLookupFillBlankNetworks && chainId && !normalizeCell(data[rowIndex][19])) {
            data[rowIndex][19] = chainId;
            changed = true;
            entryChanged = true;
            networkRowsFilled += 1;
          }

          if (this.dateLookupFillBlankNetworks && chainName && !normalizeCell(data[rowIndex][20])) {
            data[rowIndex][20] = chainName;
            changed = true;
            entryChanged = true;
            networkRowsFilled += 1;
          }
        });

        if (normalizedTxHash && normalizeComparisonKey(normalizedTxHash) !== normalizeComparisonKey(data[draft.rowIndex]?.[1])) {
          data[draft.rowIndex][1] = normalizedTxHash;
          changed = true;
          entryChanged = true;
          normalizedHashCount += 1;
        }

        if (entryChanged) entriesUpdated += 1;
      });

      if (!changed) {
        return { changed: false, entriesUpdated, dateRowsFilled, networkRowsFilled, normalizedHashCount };
      }

      this.writeSheetStateById(mainSheet.id, {
        ...state,
        data,
        frozenTop: 1
      });

      this.markCaseDataMutated('transaction_dates_filled', {
        entriesUpdated,
        dateRowsFilled,
        networkRowsFilled,
        normalizedHashCount
      });

      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
      }

      return { changed: true, entriesUpdated, dateRowsFilled, networkRowsFilled, normalizedHashCount };
    },

    refreshDateChronologyCheckResults() {
      const summary = this.refreshCaseSummary();
      const mainData = this.getMatrixForLogicalSheet('main');
      const analysis = analyzeStartingMainTransactions(mainData);
      const startingMainTransactionsReady = analysis.mainTransactionCount > 0 && analysis.trashRowCount === 0;
      const entries = parseCaseEntries(mainData);
      const entryByStartRow = new Map(entries.map(entry => [entry.startRow, entry]));
      const mainDateInfos = analysis.mainTransactionRows.map((item) => {
        const entry = entryByStartRow.get(item.rowIndex) || { rows: [{ rowIndex: item.rowIndex, row: item.row }] };
        return analyzeEntryUtcDateCompletion(entry.rows);
      });
      const missingOrInvalidDateCount = mainDateInfos.filter(info => !info.complete).length;
      const parsedDates = mainDateInfos.filter(info => info.complete && info.canonicalDate).map(info => parseCaseUtcDate(info.canonicalDate).timestamp).filter(value => Number.isFinite(value));
      const chronologyPass = missingOrInvalidDateCount === 0 && parsedDates.every((value, index) => index === 0 || value >= parsedDates[index - 1]);
      const datesPass = startingMainTransactionsReady && missingOrInvalidDateCount === 0;

      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        dates: this.buildCheckResult(
          'dates',
          !startingMainTransactionsReady ? 'warn' : datesPass ? 'pass' : 'fail',
          !startingMainTransactionsReady
            ? 'Confirm starting main transactions before checking dates.'
            : datesPass
              ? 'Starting/main transaction rows have valid Column E UTC dates. Subtransaction rows are not checked at this stage.'
              : `${missingOrInvalidDateCount} starting/main transaction row${missingOrInvalidDateCount === 1 ? '' : 's'} are missing or have invalid Column E UTC dates. Subtransaction rows are not checked at this stage.`
        ),
        chronology: this.buildCheckResult(
          'chronology',
          !startingMainTransactionsReady || !datesPass ? 'warn' : chronologyPass ? 'pass' : 'fail',
          !startingMainTransactionsReady
            ? 'No confirmed starting main transactions to order yet.'
            : !datesPass
              ? 'Fill valid UTC dates for starting/main transactions before chronology can be confirmed.'
              : chronologyPass
                ? 'Starting/main transaction blocks appear chronologically ordered. Subtransaction rows travel with their parent block.'
                : 'Starting/main transaction blocks are not chronologically ordered by Column E.'
        )
      };
      this.persistCaseSupportState();

      return {
        summary,
        startingMainTransactionsReady,
        datesPass,
        chronologyPass,
        missingOrInvalidDateCount
      };
    },

    refreshMainPathLabelCheckResult() {
      const mainData = this.getMatrixForLogicalSheet('main');
      const labelAnalysis = analyzeNumericMainPathLabels(mainData);
      const pathLabelsPass = labelAnalysis.pass;
      const exampleText = labelAnalysis.examples.length > 0
        ? ` Examples: ${labelAnalysis.examples.map(example => `${abbreviateCaseBuddyHash(example.hash)} row ${example.row}, expected ${example.expected}${example.current ? `, current ${example.current}` : ''}`).join(' | ')}`
        : '';

      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        path_labels: this.buildCheckResult(
          'path_labels',
          labelAnalysis.total === 0 ? 'warn' : pathLabelsPass ? 'pass' : 'fail',
          labelAnalysis.total === 0
            ? 'No eligible top-level main transaction entries were found for path-label checking.'
            : pathLabelsPass
              ? `${labelAnalysis.total} top-level main transaction entr${labelAnalysis.total === 1 ? 'y has' : 'ies have'} standard numeric Column A labels in UTC chronological order.`
              : `${labelAnalysis.completeCount}/${labelAnalysis.total} top-level main transaction entries have standard numeric Column A labels. Missing: ${labelAnalysis.missingCount}; invalid: ${labelAnalysis.invalidCount}; sequence issues: ${labelAnalysis.sequenceIssueCount}; missing UTC dates: ${labelAnalysis.missingDateCount}; continuation label cells: ${labelAnalysis.continuationLabelCount}.${exampleText}`,
          {
            total: labelAnalysis.total,
            complete: labelAnalysis.completeCount,
            missing: labelAnalysis.missingCount,
            invalid: labelAnalysis.invalidCount,
            sequenceIssues: labelAnalysis.sequenceIssueCount,
            missingDates: labelAnalysis.missingDateCount,
            continuationLabelCells: labelAnalysis.continuationLabelCount
          }
        )
      };
      this.persistCaseSupportState();
      return { pathLabelsPass, labelAnalysis };
    },

    async runMissingDateLookup() {
      if (this.dateLookupRunning) return;

      const selectedRows = this.dateLookupRows.filter(row => row.include && row.needsDate && row.hash);
      if (selectedRows.length === 0) {
        this.dateLookupStatus = 'No missing-date starting/main transaction rows are selected.';
        return;
      }

      const localRows = selectedRows.filter(row => row.entryCanonicalDate);
      const backendRows = selectedRows.filter(row => !row.entryCanonicalDate);
      let backendReady = false;

      if (backendRows.length > 0) {
        if (!this.authSessionLoaded) {
          await this.loadAuthSession();
        }

        if (this.authSession.isAuthenticated) {
          const token = await this.getCaseBuddyApiBearerToken();
          backendReady = !!token;
          if (!backendReady && localRows.length === 0) {
            this.dateLookupStatus = 'Your login session is loaded, but no bearer token was found for the API request.';
            this.showCaseToast(this.dateLookupStatus);
            return;
          }
        } else if (localRows.length === 0) {
          this.dateLookupStatus = 'Please sign in before using backend date lookup tools. Dates already present inside an entry can still be propagated locally.';
          this.showCaseToast(this.dateLookupStatus);
          return;
        }
      }

      this.dateLookupRunning = true;
      this.dateLookupStatus = `Completing UTC dates for ${selectedRows.length} starting/main transaction${selectedRows.length === 1 ? '' : 's'}…`;
      let resolvedLocal = 0;
      let resolvedLookup = 0;
      let unresolved = 0;
      let normalizedHashCount = 0;
      const resolvedDrafts = [];

      try {
        localRows.forEach((row) => {
          const dateToApply = normalizeCaseUtcDate(row.entryCanonicalDate);
          if (!dateToApply) {
            unresolved += 1;
            this.updateDateLookupRow(row.id, {
              status: 'unresolved',
              statusLabel: 'Unresolved',
              message: 'Existing entry date could not be normalized.',
              dateToApply: ''
            });
            return;
          }

          resolvedLocal += 1;
          const updatedDraft = {
            ...row,
            dateToApply,
            include: false,
            status: 'resolved',
            statusLabel: 'Propagated',
            source: 'existing-entry-date',
            message: 'Propagated a valid date already present in this main transaction entry.'
          };
          resolvedDrafts.push(updatedDraft);
          this.updateDateLookupRow(row.id, updatedDraft);
        });

        if (backendRows.length > 0 && !backendReady) {
          backendRows.forEach((row) => {
            unresolved += 1;
            this.updateDateLookupRow(row.id, {
              status: 'error',
              statusLabel: 'Sign in needed',
              message: 'Backend lookup requires a logged-in Case Buddy web app session.',
              include: false
            });
          });
        }

        if (backendReady) {
          for (let index = 0; index < backendRows.length; index += 1) {
            const row = backendRows[index];
            this.updateDateLookupRow(row.id, {
              status: 'running',
              statusLabel: `Lookup ${index + 1}/${backendRows.length}`,
              message: '',
              lookupResult: null
            });
            this.dateLookupStatus = `Looking up date ${index + 1}/${backendRows.length}: ${abbreviateCaseBuddyHash(row.hash)}`;

            try {
              const payload = await this.lookupTransactionDate(row);
              const dateToApply = payload?.result?.found ? formatNetworkLookupUtcDate(payload.result) : '';
              if (payload.status === 'success' && payload.result?.found && dateToApply) {
                const result = payload.result;
                resolvedLookup += 1;
                if (result.normalizedTxHash && normalizeComparisonKey(result.normalizedTxHash) !== normalizeComparisonKey(row.hash)) {
                  normalizedHashCount += 1;
                }
                const updatedDraft = {
                  ...row,
                  dateToApply,
                  lookupResult: result,
                  include: false,
                  status: 'resolved',
                  statusLabel: 'Date found',
                  source: normalizeCell(result.source),
                  tried: Array.isArray(payload.tried) ? payload.tried : [],
                  message: result.inputWasNormalized ? 'Hash normalized before applying date.' : 'UTC date confirmed.'
                };
                resolvedDrafts.push(updatedDraft);
                this.updateDateLookupRow(row.id, updatedDraft);
              } else {
                unresolved += 1;
                this.updateDateLookupRow(row.id, {
                  status: 'unresolved',
                  statusLabel: 'Not found',
                  message: payload?.message || 'No UTC date was returned for this transaction.',
                  tried: Array.isArray(payload?.tried) ? payload.tried : [],
                  lookupResult: payload?.result || null
                });
              }
            } catch (error) {
              unresolved += 1;
              this.updateDateLookupRow(row.id, {
                status: 'error',
                statusLabel: 'Error',
                message: error?.message || 'Lookup failed.',
                lookupResult: null
              });
            }

            if (index < backendRows.length - 1) {
              await sleep(CASE_BUDDY_NETWORK_LOOKUP_DELAY_MS);
            }
          }
        }

        const filled = resolvedLocal + resolvedLookup;
        const applyStats = this.applyDateLookupResultsToMainSheet(resolvedDrafts);
        const dateCheckState = this.refreshDateChronologyCheckResults();

        this.recordWorkflowToolRun('lookup_dates_auto', {
          requested: selectedRows.length,
          filled,
          resolvedLocal,
          resolvedLookup,
          unresolved,
          normalizedHashCount,
          ...applyStats
        });

        this.loadOutputRows(buildDateLookupAuditRows(this.dateLookupRows, {
          requested: selectedRows.length,
          filled,
          resolvedLocal,
          resolvedLookup,
          unresolved
        }), {
          title: 'UTC date lookup audit',
          detail: 'Audit only. Successful UTC date lookup values were written directly to Main Data. Do not append this audit to case rows.',
          sourceActionId: 'lookup_dates_auto',
          target: 'audit-only'
        });

        if (dateCheckState.datesPass) {
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_TRANSACTION_DATES_FILLED, 'lookup_dates_auto', { silent: true });
        }

        if (dateCheckState.datesPass && dateCheckState.chronologyPass) {
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_MAIN_ENTRIES_DATED_AND_ORDERED, 'lookup_dates_auto', { silent: true });
        }

        this.dateLookupLastRunSummary = {
          requested: selectedRows.length,
          filled,
          resolvedLocal,
          resolvedLookup,
          unresolved,
          normalizedHashCount,
          ...applyStats
        };
        this.refreshDateLookupSortPreview();
        this.dateLookupStatus = `Filled UTC dates for ${filled} of ${selectedRows.length} selected starting/main transaction${selectedRows.length === 1 ? '' : 's'}. ${unresolved > 0 ? `${unresolved} still need manual review.` : 'All selected rows were updated or already had a date to propagate.'} ${this.dateLookupSortPreview?.chronologyPass ? 'Date order is ready.' : 'Press Sort By Date before assigning path labels.'}`;
        this.showCaseToast(this.dateLookupStatus);
      } finally {
        this.dateLookupRunning = false;
      }
    },

    buildMainEntryDateSortBlocks(data = []) {
      const normalizedData = normalizeCaseMatrix(data);
      const analysis = analyzeStartingMainTransactions(normalizedData);
      const mainRows = analysis.mainTransactionRows.slice().sort((left, right) => left.rowIndex - right.rowIndex);
      let lastMeaningfulRow = 0;
      normalizedData.forEach((row, index) => {
        if (index > 0 && rowHasMeaningfulData(row)) lastMeaningfulRow = index;
      });

      if (mainRows.length === 0) {
        return { analysis, header: normalizedData[0] || [], leadingRows: [], blocks: [], lastMeaningfulRow, originalLength: normalizedData.length };
      }

      const firstMainRow = mainRows[0].rowIndex;
      const leadingRows = normalizedData.slice(1, firstMainRow).map(row => Array.isArray(row) ? row.slice() : []);
      const blocks = mainRows.map((item, index) => {
        const nextMainRow = mainRows[index + 1]?.rowIndex;
        const startRow = item.rowIndex;
        const endRow = Math.min(
          nextMainRow == null ? lastMeaningfulRow : nextMainRow - 1,
          lastMeaningfulRow
        );
        const rows = normalizedData.slice(startRow, Math.max(startRow, endRow) + 1).map(row => Array.isArray(row) ? row.slice() : []);
        const dateInfo = parseCaseUtcDate(item.row?.[4]);
        const hash = normalizeCell(item.row?.[1]);

        return {
          id: `main-sort-block-${startRow}-${normalizeComparisonKey(hash) || index}`,
          originalOrder: index,
          startRow,
          endRow: Math.max(startRow, endRow),
          rows,
          pathLabel: normalizeCell(item.row?.[0]),
          hash,
          dateInfo
        };
      });

      return {
        analysis,
        header: Array.isArray(normalizedData[0]) ? normalizedData[0].slice() : [],
        leadingRows,
        blocks,
        lastMeaningfulRow,
        originalLength: normalizedData.length
      };
    },

    sortMainEntriesByDate(options = {}) {
      const keepDateModalOpen = !!options.keepDateModalOpen;
      this.ensureCaseWorkbookScaffold({ silent: true });
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet) {
        this.showCaseToast('Could not find the Main Data sheet.');
        return;
      }

      const state = this.getSheetStateById(mainSheet.id);
      const data = normalizeCaseMatrix(state.data);
      const sortModel = this.buildMainEntryDateSortBlocks(data);
      const blocks = sortModel.blocks || [];

      if (blocks.length === 0) {
        const message = 'No starting/main transaction blocks were detected to sort.';
        this.refreshDateLookupSortPreview?.();
        if (keepDateModalOpen) this.dateLookupStatus = message;
        this.showCaseToast(message);
        return { blocksSorted: 0, orderChanged: false, checkState: null };
      }

      if (blocks.length === 1) {
        const checkState = this.refreshDateChronologyCheckResults();
        if (checkState.datesPass && checkState.chronologyPass) {
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_MAIN_ENTRIES_ORDERED_BY_DATE, 'sort_main_entries_by_date_auto', { silent: true });
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_MAIN_ENTRIES_DATED_AND_ORDERED, 'sort_main_entries_by_date_auto', { silent: true });
        }
        this.refreshDateLookupSortPreview?.();
        const message = 'Only one starting/main transaction block was detected; no chronological reorder was needed.';
        if (keepDateModalOpen) this.dateLookupStatus = `${message} You can move on to path labels once Column E is valid.`;
        this.showCaseToast(message);
        return { blocksSorted: 1, orderChanged: false, checkState };
      }

      const sortedBlocks = blocks.slice().sort((left, right) => {
        const leftValid = !!left.dateInfo?.isValid;
        const rightValid = !!right.dateInfo?.isValid;
        if (leftValid && rightValid) {
          if (left.dateInfo.timestamp !== right.dateInfo.timestamp) {
            return left.dateInfo.timestamp - right.dateInfo.timestamp;
          }
          return left.originalOrder - right.originalOrder;
        }
        if (leftValid && !rightValid) return -1;
        if (!leftValid && rightValid) return 1;
        return left.originalOrder - right.originalOrder;
      });

      const orderChanged = sortedBlocks.some((block, index) => block.id !== blocks[index]?.id);
      const rebuiltData = [
        sortModel.header.length > 0 ? sortModel.header : CASE_COLUMN_HEADERS.slice(),
        ...sortModel.leadingRows.map(row => Array.isArray(row) ? row.slice() : []),
        ...sortedBlocks.flatMap(block => block.rows.map(row => Array.isArray(row) ? row.slice() : []))
      ];

      while (rebuiltData.length < Math.max(CASE_DEFAULT_ROWS, sortModel.originalLength)) {
        rebuiltData.push(Array(CASE_DEFAULT_COLS).fill(''));
      }

      if (orderChanged) {
        this.writeSheetStateById(mainSheet.id, {
          ...state,
          data: normalizeCaseMatrix(rebuiltData),
          frozenTop: 1
        });

        this.markCaseDataMutated('main_entries_sorted_by_date', {
          blocksSorted: blocks.length,
          undatedOrInvalidBlocks: blocks.filter(block => !block.dateInfo?.isValid).length
        });

        if (this.autoSaveEnabled) {
          this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
        }
      }

      const checkState = this.refreshDateChronologyCheckResults();
      this.recordWorkflowToolRun('sort_main_entries_by_date_auto', {
        blocksSorted: blocks.length,
        orderChanged,
        undatedOrInvalidBlocks: blocks.filter(block => !block.dateInfo?.isValid).length
      });

      this.loadOutputRows(buildDateSortAuditRows(blocks, sortedBlocks), {
        title: 'Main entry date sort audit',
        detail: 'Audit only. Main Data was chronologically reordered by starting/main transaction blocks; child and spacer rows stayed with their current parent block.',
        sourceActionId: 'sort_main_entries_by_date_auto',
        target: 'audit-only'
      });

      if (checkState.chronologyPass) {
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_MAIN_ENTRIES_ORDERED_BY_DATE, 'sort_main_entries_by_date_auto', { silent: true });
      }

      if (checkState.datesPass && checkState.chronologyPass) {
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_MAIN_ENTRIES_DATED_AND_ORDERED, 'sort_main_entries_by_date_auto', { silent: true });
      }

      if (!keepDateModalOpen) {
        this.activateLogicalSheet('main');
      }
      this.refreshDateLookupSortPreview?.();
      const message = orderChanged
        ? `Sorted ${blocks.length} starting/main transaction block${blocks.length === 1 ? '' : 's'} by UTC date. Child/subtransaction rows stayed with their current parent block.`
        : 'Starting/main transaction blocks already appeared chronologically ordered.';
      if (keepDateModalOpen) {
        this.refreshDateLookupRows?.();
        this.dateLookupStatus = `${message} You can now assign main transaction path labels if the date and chronology checks are green.`;
      }
      this.showCaseToast(message);
      return { blocksSorted: blocks.length, orderChanged, checkState };
    },

    async sortMainEntriesByDateFromDateModal() {
      return this.runCaseModalOperation({
        title: 'Sorting main entries…',
        message: 'Reordering starting/main transaction blocks by Column E UTC date before path-label assignment.',
        target: 'date-sort-modal-action',
        finishMessage: 'Date sort check complete.'
      }, async () => {
        const result = this.sortMainEntriesByDate({ keepDateModalOpen: true });
        await this.yieldToCaseUi?.({ paint: true, minDelayMs: 8 });
        return result !== false;
      });
    },

    async openMainPathLabelModal() {
      return this.runCaseModalOperation({
        title: 'Opening path-label assignment…',
        message: 'Reviewing current main transactions for proposed path labels.',
        target: 'main-path-label-modal',
        finishMessage: 'Path-label assignment view ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.mainPathLabelStatus = '';
        this.mainPathLabelLastRunSummary = null;
        this.showMainPathLabelModal = true;
        await this.yieldToCaseUi();
        this.refreshMainPathLabelRows();
        return true;
      });
    },

    cancelMainPathLabelModal() {
      if (this.mainPathLabelAssignRunning) return;
      this.showMainPathLabelModal = false;
    },

    buildMainPathLabelRows() {
      const mainData = this.getMatrixForLogicalSheet('main');
      const descriptors = sortTopLevelMainEntriesForLabelPlan(buildTopLevelMainEntryDescriptors(mainData));
      return descriptors.map((entry, index) => ({
        ...entry,
        order: index + 1,
        proposedLabel: String(index + 1)
      }));
    },

    openRawDataImportModal() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.rawDataImportPaste = '';
      this.rawDataImportRows = [];
      this.rawDataImportStagedSets = [];
      this.rawDataImportAddHeadersIfMissing = true;
      this.rawDataImportStatus = this.rawDataCoverageStageSummary;
      this.showRawDataImportModal = true;
    },

    cancelRawDataImportModal() {
      this.showRawDataImportModal = false;
    },

    clearRawDataImportDraft() {
      this.rawDataImportPaste = '';
      this.rawDataImportRows = [];
      this.rawDataImportStagedSets = [];
      this.rawDataImportStatus = this.rawDataCoverageStageSummary;
    },

    refreshRawDataImportCoverage() {
      if (this.rawDataImportPaste) {
        this.parseRawDataImportPaste();
      } else {
        this.rawDataImportStatus = this.rawDataCoverageStageSummary;
      }
      this.refreshCaseSummary();
    },

    async copyRawCoverageHash(row = {}) {
      const hash = normalizeCell(row.hash);
      if (!hash) {
        this.showCaseToast('No transaction hash is available to copy.');
        return false;
      }
      return this.copyTextToClipboard(hash, 'Full transaction hash copied.');
    },

    async copyRawCoverageToAddress(row = {}) {
      const toAddress = normalizeCell(row.toAddress);
      if (!toAddress) {
        this.showCaseToast('No To Address is available for this raw-data target.');
        return false;
      }
      return this.copyTextToClipboard(toAddress, 'Related To Address copied.');
    },

    async copyRawCoverageLookupKit(row = {}) {
      const label = normalizeCell(row.pathLabel) || (row.spreadsheetRowNumber ? `Row ${row.spreadsheetRowNumber}` : 'Raw-data target');
      const hash = normalizeCell(row.hash);
      const toAddress = normalizeCell(row.toAddress);
      const lines = [
        `Main transaction: ${label}`,
        row.spreadsheetRowNumber ? `Main sheet row: ${row.spreadsheetRowNumber}` : '',
        hash ? `Transaction hash: ${hash}` : 'Transaction hash: —',
        toAddress ? `Related To Address: ${toAddress}` : 'Related To Address: none recorded on Main Data',
        row.statusLabel ? `Coverage status: ${row.statusLabel}` : '',
        row.message ? `Note: ${row.message}` : ''
      ].filter(Boolean);
      return this.copyTextToClipboard(lines.join('\n'), 'Raw-data lookup kit copied.');
    },


    getWayFinderHandoffCaseId() {
      return buildWayFinderHandoffCaseId({
        currentProjectFileId: this.currentProjectFileId,
        caseDisplayId: this.caseDisplayId,
        caseDisplayName: this.caseDisplayName
      });
    },

    loadWayFinderHandoffSettings() {
      if (typeof window === 'undefined' || !window.localStorage) {
        this.wayFinderHandoffBridgeUrl = CASE_WAYFINDER_HANDOFF_DEFAULT_BRIDGE_URL;
        return;
      }
      try {
        const raw = window.localStorage.getItem(CASE_WAYFINDER_HANDOFF_SETTINGS_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        this.wayFinderHandoffBridgeUrl = normalizeWayFinderBridgeUrl(parsed.bridgeUrl || this.wayFinderHandoffBridgeUrl || CASE_WAYFINDER_HANDOFF_DEFAULT_BRIDGE_URL);
        this.wayFinderHandoffAutoImportCompleted = parsed.autoImportCompleted !== false;
        this.wayFinderHandoffStaleMinutes = Number(parsed.staleMinutes) || CASE_WAYFINDER_HANDOFF_STALE_MINUTES;
      } catch {
        this.wayFinderHandoffBridgeUrl = CASE_WAYFINDER_HANDOFF_DEFAULT_BRIDGE_URL;
      }
    },

    saveWayFinderHandoffSettings() {
      this.wayFinderHandoffBridgeUrl = normalizeWayFinderBridgeUrl(this.wayFinderHandoffBridgeUrl);
      if (typeof window === 'undefined' || !window.localStorage) return;
      try {
        window.localStorage.setItem(CASE_WAYFINDER_HANDOFF_SETTINGS_KEY, JSON.stringify({
          bridgeUrl: this.wayFinderHandoffBridgeUrl,
          autoImportCompleted: this.wayFinderHandoffAutoImportCompleted !== false,
          staleMinutes: Number(this.wayFinderHandoffStaleMinutes) || CASE_WAYFINDER_HANDOFF_STALE_MINUTES
        }));
      } catch {
        // Settings persistence is optional; the connector still works for the current session.
      }
    },

    refreshWayFinderHandoffTargets() {
      const coverage = buildMainRawDataCoverage(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw')
      );
      const targets = buildWayFinderHandoffTargetsFromCoverage(coverage, {
        mainData: this.getMatrixForLogicalSheet('main'),
        caseId: this.getWayFinderHandoffCaseId(),
        caseName: this.caseDisplayName,
        caseRevision: this.caseSupportState?.caseDataRevision || 0
      });
      this.wayFinderHandoffTargets = mergeWayFinderHandoffJobsIntoTargets(targets, this.wayFinderHandoffJobs);
      const selected = { ...(this.wayFinderHandoffSelectedById || {}) };
      this.wayFinderHandoffTargets.forEach((target) => {
        if (selected[target.id] === undefined) selected[target.id] = !!target.canQueue;
      });
      Object.keys(selected).forEach((key) => {
        if (!this.wayFinderHandoffTargets.some(target => target.id === key)) delete selected[key];
      });
      this.wayFinderHandoffSelectedById = selected;
      return coverage;
    },

    async openWayFinderHandoffModal() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.loadWayFinderHandoffSettings();
      this.wayFinderHandoffStatus = 'Preparing WayFinder handoff queue view…';
      this.showWayFinderHandoffModal = true;
      await this.yieldToCaseUi?.();
      this.refreshWayFinderHandoffTargets();
      await this.pingWayFinderHandoffBridge({ silent: true });
      await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });
      this.startWayFinderHandoffPolling();
      this.wayFinderHandoffStatus = this.wayFinderHandoffTargets.length
        ? `${this.wayFinderHandoffTargets.length} raw-data target${this.wayFinderHandoffTargets.length === 1 ? '' : 's'} can be queued or tracked.`
        : 'Raw-data coverage is complete. No WayFinder requests are needed right now.';
    },

    closeWayFinderHandoffModal() {
      this.stopWayFinderHandoffPolling();
      this.showWayFinderHandoffModal = false;
    },

    openManualRawDataImportFromWayFinder() {
      this.closeWayFinderHandoffModal();
      this.openRawDataImportModal();
    },

    async callWayFinderHandoffBridge(path, options = {}) {
      const url = buildWayFinderHandoffEndpoint(this.wayFinderHandoffBridgeUrl, path);
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timeoutMs = Number(options.timeoutMs) || 15000;
      let timeoutId = null;
      if (controller) {
        timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      }

      try {
        const response = await fetch(url, {
          method: options.method || 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {})
          },
          body: options.body === undefined ? undefined : JSON.stringify(options.body),
          signal: controller?.signal
        });
        const text = await response.text();
        let payload = null;
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch {
            payload = { text };
          }
        }
        if (!response.ok) {
          const message = normalizeCell(payload?.message || payload?.error || payload?.text) || `WayFinder bridge returned HTTP ${response.status}.`;
          throw new Error(message);
        }
        return payload || {};
      } catch (error) {
        const message = error?.name === 'AbortError'
          ? 'WayFinder bridge did not respond before the timeout.'
          : (error?.message || 'Could not reach the WayFinder bridge.');
        throw new Error(message);
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    },

    async pingWayFinderHandoffBridge(options = {}) {
      this.saveWayFinderHandoffSettings();
      this.wayFinderHandoffBusy = true;
      try {
        const payload = await this.callWayFinderHandoffBridge('casebuddy/health', { timeoutMs: 5000 });
        this.wayFinderHandoffConnected = true;
        this.wayFinderHandoffConnectionStatus = normalizeCell(payload.status || payload.message) || 'Connected';
        if (!options.silent) this.showCaseToast('WayFinder bridge connected.');
        return true;
      } catch (error) {
        this.wayFinderHandoffConnected = false;
        this.wayFinderHandoffConnectionStatus = error.message;
        if (!options.silent) this.showCaseToast(`WayFinder bridge not available: ${error.message}`);
        return false;
      } finally {
        this.wayFinderHandoffBusy = false;
      }
    },

    async runWayFinderHandoffUntilRawCoverageComplete(options = {}) {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.loadWayFinderHandoffSettings();
      const startedAt = Date.now();
      const maxWaitMs = Math.max(60000, Number(options.maxWaitMs) || 60 * 60 * 1000);
      const pollMs = Math.max(2500, Number(options.pollMs) || CASE_WAYFINDER_HANDOFF_POLL_INTERVAL_MS);
      let rowsAppendedTotal = 0;
      let lastQueuedCount = 0;

      const updateAutoStatus = (status, detail = '') => {
        this.wayFinderHandoffStatus = detail || status;
        if (typeof this.setCaseAutomationStatus === 'function') {
          this.setCaseAutomationStatus(status, detail);
        }
      };

      const shouldStop = () => !!(options.fromAuto && (!this.caseAutomation?.active || this.caseAutomation?.paused));

      updateAutoStatus('Connecting to WayFinder…', 'Checking the local WayFinder bridge before queuing raw-data requests.');
      const connected = await this.pingWayFinderHandoffBridge({ silent: true });
      if (!connected) {
        return {
          ok: false,
          reason: 'bridge-unavailable',
          message: `WayFinder bridge is not connected: ${this.wayFinderHandoffConnectionStatus || 'Failed to fetch'}`
        };
      }

      await this.resetStaleWayFinderHandoffClaims({ silent: true });
      await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });

      while (Date.now() - startedAt <= maxWaitMs) {
        if (shouldStop()) {
          return { ok: false, reason: 'user-stopped', userStopped: true, message: 'Automation was stopped before WayFinder raw-data loading finished.' };
        }

        const coverage = this.refreshWayFinderHandoffTargets();
        const missingCount = Number(coverage?.missingCount) || (this.wayFinderHandoffTargets || []).filter(target => target.status !== 'covered').length;
        if (missingCount <= 0) {
          const rawCheck = this.refreshRawDataCoverageCheckResult?.(coverage);
          if (rawCheck?.status === 'pass') {
            this.recordWorkflowSignal?.(WORKFLOW_SIGNAL_RAW_DATA_LOADED, 'wayfinder_handoff_auto_raw_data_complete', { silent: true });
          }
          updateAutoStatus('Raw-data coverage complete.', 'WayFinder results are imported into Raw Data. Auto will continue to prepare the sapling.');
          return { ok: true, reason: 'coverage-complete', rowsAppended: rowsAppendedTotal, missingCount: 0 };
        }

        const queueableTargets = (this.wayFinderHandoffTargets || []).filter(target => target.canQueue);
        if (queueableTargets.length > 0) {
          const selected = {};
          queueableTargets.forEach((target) => { selected[target.id] = true; });
          this.wayFinderHandoffSelectedById = selected;
          updateAutoStatus(
            'Queueing WayFinder raw-data requests…',
            `${queueableTargets.length} target${queueableTargets.length === 1 ? '' : 's'} will be queued for WayFinder Desktop.`
          );
          const queued = await this.queueSelectedWayFinderHandoffJobs({ silent: true });
          if (!queued) {
            return {
              ok: false,
              reason: 'queue-failed',
              message: this.wayFinderHandoffStatus || 'Could not queue WayFinder raw-data requests.'
            };
          }
          lastQueuedCount += queueableTargets.length;
          await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });
        } else {
          await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });
        }

        const imported = await this.importCompletedWayFinderHandoffJobs({ silent: true });
        if (imported) {
          rowsAppendedTotal += Math.max(0, Number(imported.rowsAppended) || 0);
          await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });
          continue;
        }

        const summary = summarizeWayFinderHandoffJobs(this.wayFinderHandoffJobs);
        const activeCount = Number(summary.queued) + Number(summary.inProgress);
        const readyCount = Number(summary.readyToImport) || 0;
        const failedCount = Number(summary.failed) || 0;
        const elapsedMinutes = Math.max(0, Math.floor((Date.now() - startedAt) / 60000));

        updateAutoStatus(
          'Waiting for WayFinder raw data…',
          `${missingCount} target${missingCount === 1 ? '' : 's'} still need coverage · ${summary.queued} queued · ${summary.inProgress} in progress · ${readyCount} ready · ${elapsedMinutes} min elapsed.`
        );

        if (activeCount <= 0 && readyCount <= 0 && queueableTargets.length <= 0 && failedCount > 0) {
          return {
            ok: false,
            reason: 'jobs-failed',
            message: `${failedCount} WayFinder handoff job${failedCount === 1 ? '' : 's'} failed and ${missingCount} target${missingCount === 1 ? '' : 's'} still need raw data. Open the handoff queue to review or requeue.`
          };
        }

        await sleep(pollMs);
      }

      return {
        ok: false,
        reason: 'timeout',
        queuedCount: lastQueuedCount,
        rowsAppended: rowsAppendedTotal,
        message: 'WayFinder raw-data handoff did not finish within the automation wait window. Leave WayFinder running, then continue Auto or use the handoff queue manually.'
      };
    },

    wayFinderHandoffSelectedTargets() {
      return (this.wayFinderHandoffTargets || []).filter(target => this.wayFinderHandoffSelectedById?.[target.id] && target.canQueue);
    },

    setAllWayFinderHandoffTargetsSelected(selected = true) {
      const next = { ...(this.wayFinderHandoffSelectedById || {}) };
      (this.wayFinderHandoffTargets || []).forEach((target) => {
        if (target.canQueue) next[target.id] = !!selected;
      });
      this.wayFinderHandoffSelectedById = next;
    },

    async queueSelectedWayFinderHandoffJobs({ force = false, silent = false } = {}) {
      const targets = this.wayFinderHandoffSelectedTargets();
      if (targets.length === 0) {
        this.wayFinderHandoffStatus = 'No queueable raw-data targets are selected.';
        return false;
      }
      this.saveWayFinderHandoffSettings();
      this.wayFinderHandoffBusy = true;
      this.wayFinderHandoffStatus = `Queueing ${targets.length} WayFinder request${targets.length === 1 ? '' : 's'}…`;
      try {
        const caseId = this.getWayFinderHandoffCaseId();
        const jobs = targets.map(buildWayFinderHandoffJobPayload);
        const payload = await this.callWayFinderHandoffBridge('casebuddy/jobs/bulk', {
          method: 'POST',
          timeoutMs: 30000,
          body: {
            case_id: caseId,
            force: !!force,
            jobs
          }
        });
        const returnedJobs = normalizeWayFinderHandoffJobs(payload);
        if (returnedJobs.length > 0) {
          const byId = new Map((this.wayFinderHandoffJobs || []).map(job => [job.jobId || `${job.transactionHash}|${job.requestedMainRowId}`, job]));
          returnedJobs.forEach((job) => {
            byId.set(job.jobId || `${job.transactionHash}|${job.requestedMainRowId}`, job);
          });
          this.wayFinderHandoffJobs = Array.from(byId.values());
        }
        this.refreshWayFinderHandoffTargets();
        this.recordWorkflowToolRun('wayfinder_handoff_queue_jobs', {
          caseId,
          queuedCount: targets.length,
          returnedJobCount: returnedJobs.length
        });
        this.wayFinderHandoffStatus = `Queued ${targets.length} WayFinder request${targets.length === 1 ? '' : 's'}. Leave WayFinder running in Case Buddy mode to process them.`;
        if (!silent) this.showCaseToast(this.wayFinderHandoffStatus);
        return true;
      } catch (error) {
        this.wayFinderHandoffConnected = false;
        this.wayFinderHandoffConnectionStatus = error.message;
        this.wayFinderHandoffStatus = `Could not queue WayFinder requests: ${error.message}`;
        return false;
      } finally {
        this.wayFinderHandoffBusy = false;
      }
    },

    async refreshWayFinderHandoffJobs(options = {}) {
      this.saveWayFinderHandoffSettings();
      if (this.wayFinderHandoffRefreshInFlight) return false;
      this.wayFinderHandoffRefreshInFlight = true;
      if (!options.silent) {
        this.wayFinderHandoffBusy = true;
        this.wayFinderHandoffStatus = 'Refreshing WayFinder queue status…';
      }
      try {
        const caseId = encodeURIComponent(this.getWayFinderHandoffCaseId());
        const payload = await this.callWayFinderHandoffBridge(`casebuddy/jobs?case_id=${caseId}&limit=500`, { timeoutMs: 15000 });
        this.wayFinderHandoffJobs = normalizeWayFinderHandoffJobs(payload);
        this.wayFinderHandoffConnected = true;
        this.wayFinderHandoffConnectionStatus = 'Connected';
        this.wayFinderHandoffLastRefreshUtc = new Date().toISOString();
        this.refreshWayFinderHandoffTargets();
        const summary = summarizeWayFinderHandoffJobs(this.wayFinderHandoffJobs);
        this.wayFinderHandoffStatus = summary.total
          ? `${summary.total} queue job${summary.total === 1 ? '' : 's'} tracked: ${summary.queued} queued, ${summary.inProgress} in progress, ${summary.readyToImport} ready to import, ${summary.imported} imported.`
          : 'No WayFinder handoff jobs are recorded for this case yet.';
        if (options.importCompleted !== false && this.wayFinderHandoffAutoImportCompleted) {
          await this.importCompletedWayFinderHandoffJobs({ silent: true });
        }
        return true;
      } catch (error) {
        this.wayFinderHandoffConnected = false;
        this.wayFinderHandoffConnectionStatus = error.message;
        if (!options.silent) this.wayFinderHandoffStatus = `Could not refresh WayFinder queue: ${error.message}`;
        return false;
      } finally {
        this.wayFinderHandoffRefreshInFlight = false;
        if (!options.silent) this.wayFinderHandoffBusy = false;
      }
    },

    startWayFinderHandoffPolling() {
      this.stopWayFinderHandoffPolling();
      this.wayFinderHandoffPolling = true;
      this.wayFinderHandoffPollTimer = setInterval(() => {
        if (!this.showWayFinderHandoffModal || this.wayFinderHandoffBusy || this.wayFinderHandoffRefreshInFlight) return;
        this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: true });
      }, CASE_WAYFINDER_HANDOFF_POLL_INTERVAL_MS);
    },

    stopWayFinderHandoffPolling() {
      this.wayFinderHandoffPolling = false;
      if (this.wayFinderHandoffPollTimer) {
        clearInterval(this.wayFinderHandoffPollTimer);
        this.wayFinderHandoffPollTimer = null;
      }
    },

    wayFinderHandoffImportableJobs() {
      return (this.wayFinderHandoffJobs || []).filter(job => job.canImport);
    },

    async importCompletedWayFinderHandoffJobs(options = {}) {
      const jobs = this.wayFinderHandoffImportableJobs();
      if (jobs.length === 0) {
        if (!options.silent) this.wayFinderHandoffStatus = 'No completed WayFinder results are ready to import.';
        return false;
      }
      this.wayFinderHandoffBusy = true;
      if (!options.silent) this.wayFinderHandoffStatus = `Importing ${jobs.length} completed WayFinder result${jobs.length === 1 ? '' : 's'} into Raw Data…`;
      try {
        const rows = [];
        jobs.forEach((job, index) => {
          const parsedRows = normalizeRawDataImportRowsFromPaste(job.resultTsv, { addHeadersIfMissing: true })
            .filter((row, rowIndex, source) => {
              if (rowHasMeaningfulData(row)) return true;
              const previousMeaningful = rowIndex > 0 && rowHasMeaningfulData(source[rowIndex - 1]);
              const nextMeaningful = rowIndex < source.length - 1 && rowHasMeaningfulData(source[rowIndex + 1]);
              return previousMeaningful && nextMeaningful;
            });
          if (parsedRows.length > 0) {
            if (rows.length > 0 || index > 0) rows.push([]);
            rows.push(...parsedRows);
          }
        });

        if (rows.length === 0) {
          this.wayFinderHandoffStatus = 'Completed WayFinder jobs did not contain importable TSV rows.';
          return false;
        }

        this.ensureCaseWorkbookScaffold({ silent: true });
        const rawSheet = this.findLogicalSheetMeta('raw');
        if (!rawSheet) {
          this.wayFinderHandoffStatus = 'Could not find the Raw Data sheet.';
          return false;
        }

        const state = this.getSheetStateById(rawSheet.id);
        const appendResult = buildRawDataAppendMatrix(state.data, rows);
        if (appendResult.rowsAppended === 0) {
          this.wayFinderHandoffStatus = 'No non-empty WayFinder rows were available to append.';
          return false;
        }

        this.writeSheetStateById(rawSheet.id, {
          ...state,
          data: appendResult.data,
          frozenTop: 1
        });

        this.markCaseDataMutated('wayfinder_handoff_raw_data_imported', {
          jobsImported: jobs.length,
          rowsAppended: appendResult.rowsAppended,
          startRow: appendResult.startRow
        });

        const coverageAfter = buildMainRawDataCoverage(this.getMatrixForLogicalSheet('main'), appendResult.data);
        const rawCheck = this.refreshRawDataCoverageCheckResult(coverageAfter);
        const auditRows = buildRawDataImportAuditRows(appendResult, coverageAfter);
        auditRows.splice(1, 0, ['Source', 'WayFinder handoff queue', 'Jobs imported', jobs.length, 'Bridge URL', normalizeWayFinderBridgeUrl(this.wayFinderHandoffBridgeUrl)]);

        this.loadOutputRows(auditRows, {
          title: 'WayFinder handoff raw-data import audit',
          detail: 'Audit trail only. Completed WayFinder handoff results were written into Raw Data; Main Data was not changed.',
          sourceActionId: 'wayfinder_handoff_import_completed_results',
          target: 'audit-only'
        });

        if (rawCheck.status === 'pass') {
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_RAW_DATA_LOADED, 'wayfinder_handoff_import_completed_results', { silent: true });
        }

        const jobIds = jobs.map(job => job.jobId).filter(Boolean);
        if (jobIds.length > 0) {
          try {
            await this.callWayFinderHandoffBridge('casebuddy/jobs/imported', {
              method: 'POST',
              timeoutMs: 15000,
              body: { job_ids: jobIds }
            });
          } catch (error) {
            this.wayFinderHandoffStatus = `Imported rows, but could not mark WayFinder jobs imported: ${error.message}`;
          }
        }

        await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });
        this.refreshRawDataImportCoverage();
        const message = rawCheck.status === 'pass'
          ? `Imported ${appendResult.rowsAppended} WayFinder raw row${appendResult.rowsAppended === 1 ? '' : 's'}; raw coverage is complete.`
          : `Imported ${appendResult.rowsAppended} WayFinder raw row${appendResult.rowsAppended === 1 ? '' : 's'}; ${coverageAfter.missingCount} target${coverageAfter.missingCount === 1 ? '' : 's'} still need raw data.`;
        this.wayFinderHandoffStatus = message;
        if (!options.silent) this.showCaseToast(message);
        return {
          ok: true,
          jobsImported: jobs.length,
          rowsAppended: appendResult.rowsAppended,
          rawCoverageStatus: rawCheck.status,
          missingCount: Number(coverageAfter.missingCount) || 0
        };
      } finally {
        this.wayFinderHandoffBusy = false;
      }
    },

    wayFinderHandoffSelectedJobIds() {
      return (this.wayFinderHandoffJobs || [])
        .filter(job => this.wayFinderHandoffSelectedJobById?.[job.jobId])
        .map(job => job.jobId)
        .filter(Boolean);
    },

    setWayFinderHandoffJobSelected(jobId, selected = true) {
      if (!jobId) return;
      this.wayFinderHandoffSelectedJobById = {
        ...(this.wayFinderHandoffSelectedJobById || {}),
        [jobId]: !!selected
      };
    },

    async dismissSelectedWayFinderHandoffJobs() {
      const jobIds = this.wayFinderHandoffSelectedJobIds();
      if (jobIds.length === 0) {
        this.wayFinderHandoffStatus = 'Select queued or unwanted jobs to dismiss first.';
        return false;
      }
      this.wayFinderHandoffBusy = true;
      this.wayFinderHandoffStatus = `Dismissing ${jobIds.length} WayFinder job${jobIds.length === 1 ? '' : 's'}…`;
      try {
        await this.callWayFinderHandoffBridge('casebuddy/jobs/dismiss', {
          method: 'POST',
          timeoutMs: 15000,
          body: { job_ids: jobIds, reason: 'Dismissed from Case Buddy Web App' }
        });
        this.wayFinderHandoffSelectedJobById = {};
        await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });
        this.wayFinderHandoffStatus = `Dismissed ${jobIds.length} WayFinder job${jobIds.length === 1 ? '' : 's'}.`;
        this.showCaseToast(this.wayFinderHandoffStatus);
        return true;
      } catch (error) {
        this.wayFinderHandoffStatus = `Could not dismiss WayFinder jobs: ${error.message}`;
        return false;
      } finally {
        this.wayFinderHandoffBusy = false;
      }
    },

    async resetStaleWayFinderHandoffClaims(options = {}) {
      const staleMinutes = Number(this.wayFinderHandoffStaleMinutes) || CASE_WAYFINDER_HANDOFF_STALE_MINUTES;
      this.wayFinderHandoffBusy = true;
      if (!options.silent) this.wayFinderHandoffStatus = `Resetting in-progress jobs older than ${staleMinutes} minute${staleMinutes === 1 ? '' : 's'}…`;
      try {
        const payload = await this.callWayFinderHandoffBridge('casebuddy/jobs/reset-stale', {
          method: 'POST',
          timeoutMs: 15000,
          body: { case_id: this.getWayFinderHandoffCaseId(), stale_minutes: staleMinutes }
        });
        await this.refreshWayFinderHandoffJobs({ silent: true, importCompleted: false });
        const resetCount = Number(payload.reset_count ?? payload.resetCount) || 0;
        this.wayFinderHandoffStatus = resetCount > 0
          ? `Reset ${resetCount} stale in-progress job${resetCount === 1 ? '' : 's'} back to queued.`
          : 'No stale in-progress jobs needed resetting.';
        if (!options.silent) this.showCaseToast(this.wayFinderHandoffStatus);
        return true;
      } catch (error) {
        this.wayFinderHandoffStatus = `Could not reset stale WayFinder jobs: ${error.message}`;
        return false;
      } finally {
        this.wayFinderHandoffBusy = false;
      }
    },

    async copyWayFinderHandoffTargetKit(target = {}) {
      const lines = [
        `Case: ${this.caseDisplayName}`,
        `Main transaction: ${target.pathLabel || `Row ${target.spreadsheetRowNumber || ''}`}`.trim(),
        target.spreadsheetRowNumber ? `Main sheet row: ${target.spreadsheetRowNumber}` : '',
        target.hash ? `Transaction hash: ${target.hash}` : '',
        target.toAddress ? `Related To Address: ${target.toAddress}` : 'Related To Address: none recorded on Main Data',
        target.chainName || target.chainId ? `Network: ${target.chainName || target.chainId}` : '',
        target.hopLogic ? `Hop logic: ${target.hopLogic}` : '',
        target.message ? `Coverage note: ${target.message}` : ''
      ].filter(Boolean);
      return this.copyTextToClipboard(lines.join('\n'), 'WayFinder lookup kit copied.');
    },

    async copyWayFinderHandoffJobResult(job = {}) {
      if (!job.resultTsv) {
        this.showCaseToast('This WayFinder job does not have result TSV yet.');
        return false;
      }
      return this.copyTextToClipboard(job.resultTsv, 'WayFinder result TSV copied.');
    },

    buildRawDataImportStagedSetViews(rows = []) {
      const sets = buildRawDataSetsFromMatrix(rows);
      const mainData = this.getMatrixForLogicalSheet('main');
      const mainAnalysis = analyzeStartingMainTransactions(mainData);
      const entriesByStartRow = new Map(parseCaseEntries(mainData).map(entry => [entry.startRow, entry]));
      const mainTargets = mainAnalysis.mainTransactionRows.map(({ rowIndex, row }, index) => {
        const entry = entriesByStartRow.get(rowIndex) || { rows: [{ rowIndex, row }], hash: normalizeCell(row?.[1]) };
        const entryRows = Array.isArray(entry.rows) && entry.rows.length > 0 ? entry.rows : [{ rowIndex, row }];
        const hash = normalizeCell(row?.[1] || entry.hash);
        const toAddress = normalizeCell(row?.[3]) || firstNonEmptyEntryValue(entryRows, 3);
        return {
          id: `raw-import-target-${rowIndex}-${index}`,
          rowIndex,
          spreadsheetRowNumber: rowIndex + 1,
          pathLabel: normalizeCell(row?.[0]),
          hash,
          hashKey: normalizeComparisonKey(hash),
          toAddress,
          toAddressKey: normalizeComparisonKey(toAddress)
        };
      }).filter(target => target.hash);

      return sets.map((set) => {
        const matchedMainRows = mainTargets.filter(target => target.hashKey === set.rootHashKey && (!target.toAddressKey || target.toAddressKey === set.rootToAddressKey));
        const hashOnlyRows = mainTargets.filter(target => target.hashKey === set.rootHashKey);
        const status = matchedMainRows.length > 0 ? 'matched' : hashOnlyRows.length > 0 ? 'to-mismatch' : 'unmatched';
        const statusLabel = status === 'matched' ? 'Matches main' : status === 'to-mismatch' ? 'Hash found, To mismatch' : 'No current main match';
        const matchedMainLabel = matchedMainRows.map(target => target.pathLabel || `Row ${target.spreadsheetRowNumber}`).join(', ');
        const message = status === 'matched'
          ? 'This staged raw root can cover the matching main transaction after import.'
          : status === 'to-mismatch'
            ? 'The hash exists in Main Data, but the root To Address does not match the main transaction To Address.'
            : 'This raw set can still be saved, but it does not currently cover a detected starting/main transaction.';

        return {
          ...set,
          status,
          statusLabel,
          matchedMainLabel,
          message
        };
      });
    },

    parseRawDataImportPaste() {
      const rows = normalizeRawDataImportRowsFromPaste(this.rawDataImportPaste, {
        addHeadersIfMissing: this.rawDataImportAddHeadersIfMissing
      });
      this.rawDataImportRows = rows;
      this.rawDataImportStagedSets = this.buildRawDataImportStagedSetViews(rows);

      if (!String(this.rawDataImportPaste || '').trim()) {
        this.rawDataImportStatus = this.rawDataCoverageStageSummary;
      } else if (rows.length === 0) {
        this.rawDataImportStatus = 'No TSV rows were parsed from the pasted raw data.';
      } else if (this.rawDataImportStagedSets.length === 0) {
        this.rawDataImportStatus = 'Rows were parsed, but no valid raw root set was detected. Confirm there is a header row and a root transaction row with Column B filled and Column O blank.';
      } else {
        this.rawDataImportStatus = this.rawDataImportStageSummary;
      }

      return rows;
    },

    buildRawDataCoverageCheckResult(coverage = null) {
      const effectiveCoverage = coverage || buildMainRawDataCoverage(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw')
      );
      const analysis = effectiveCoverage.mainTransactionAnalysis || analyzeStartingMainTransactions(this.getMatrixForLogicalSheet('main'));
      const startingMainTransactionsReady = analysis.mainTransactionCount > 0 && analysis.trashRowCount === 0;

      return this.buildCheckResult(
        'raw_data',
        !startingMainTransactionsReady ? 'warn' : effectiveCoverage.ready ? 'pass' : 'fail',
        !startingMainTransactionsReady
          ? 'Confirm starting main transactions before checking raw-data coverage.'
          : effectiveCoverage.ready
            ? `${effectiveCoverage.coveredCount} starting/main transaction${effectiveCoverage.coveredCount === 1 ? '' : 's'} have matching raw-data root sets. If duplicate raw sets exist, the lower matching set is used.`
            : `${effectiveCoverage.missingCount} of ${effectiveCoverage.totalMainTransactions} starting/main transaction${effectiveCoverage.totalMainTransactions === 1 ? '' : 's'} still need matching raw data. Column D To Address must match when the main transaction has one.`,
        {
          totalMainTransactions: effectiveCoverage.totalMainTransactions,
          coveredCount: effectiveCoverage.coveredCount,
          missingCount: effectiveCoverage.missingCount,
          mismatchCount: effectiveCoverage.mismatchCount,
          rawSetCount: effectiveCoverage.rawSets?.length || 0
        }
      );
    },

    refreshRawDataCoverageCheckResult(coverage = null) {
      const result = this.buildRawDataCoverageCheckResult(coverage);
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        raw_data: result
      };
      this.persistCaseSupportState();
      this.refreshCaseSummary();
      return result;
    },

    appendRawDataImportToRawSheet() {
      const rows = this.parseRawDataImportPaste();
      if (rows.length === 0 || this.rawDataImportStagedSets.length === 0) {
        this.rawDataImportStatus = 'Paste raw TSV with at least one valid header + root transaction set before appending.';
        return;
      }

      this.ensureCaseWorkbookScaffold({ silent: true });
      const rawSheet = this.findLogicalSheetMeta('raw');
      if (!rawSheet) {
        this.rawDataImportStatus = 'Could not find the Raw Data sheet.';
        return;
      }

      const state = this.getSheetStateById(rawSheet.id);
      const appendResult = buildRawDataAppendMatrix(state.data, rows);
      if (appendResult.rowsAppended === 0) {
        this.rawDataImportStatus = 'No non-empty raw rows were available to append.';
        return;
      }

      this.writeSheetStateById(rawSheet.id, {
        ...state,
        data: appendResult.data,
        frozenTop: 1
      });

      this.markCaseDataMutated('raw_data_imported_for_main_transactions', {
        rowsAppended: appendResult.rowsAppended,
        rawSetsStaged: this.rawDataImportStagedSets.length,
        startRow: appendResult.startRow
      });

      const coverageAfter = buildMainRawDataCoverage(this.getMatrixForLogicalSheet('main'), appendResult.data);
      const rawCheck = this.refreshRawDataCoverageCheckResult(coverageAfter);
      const auditRows = buildRawDataImportAuditRows(appendResult, coverageAfter);

      this.loadOutputRows(auditRows, {
        title: 'Raw data import audit',
        detail: 'Audit trail only. Raw rows were written directly to the Raw Data sheet; Main Data was not changed.',
        sourceActionId: 'load_raw_data_for_main_transactions',
        target: 'audit-only'
      });

      if (rawCheck.status === 'pass') {
        this.recordWorkflowSignal(WORKFLOW_SIGNAL_RAW_DATA_LOADED, 'load_raw_data_for_main_transactions', { silent: true });
      }

      this.rawDataImportLastRunSummary = {
        rowsAppended: appendResult.rowsAppended,
        rawSetsStaged: this.rawDataImportStagedSets.length,
        coverageComplete: rawCheck.status === 'pass',
        missingCount: coverageAfter.missingCount
      };

      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile({ persistInput: true, persistOutput: true, markSavedAt: true });
      }

      const toastMessage = rawCheck.status === 'pass'
        ? `Appended ${appendResult.rowsAppended} raw row${appendResult.rowsAppended === 1 ? '' : 's'}; all starting/main transactions now have raw coverage.`
        : `Appended ${appendResult.rowsAppended} raw row${appendResult.rowsAppended === 1 ? '' : 's'}; ${coverageAfter.missingCount} starting/main transaction${coverageAfter.missingCount === 1 ? '' : 's'} still need raw coverage.`;

      if (this.rawDataImportKeepOpenAfterAppend) {
        this.rawDataImportPaste = '';
        this.rawDataImportRows = [];
        this.rawDataImportStagedSets = [];
        this.rawDataImportStatus = rawCheck.status === 'pass'
          ? 'Raw coverage is complete. You can close this panel or continue reviewing the Raw Data sheet.'
          : `${coverageAfter.missingCount} starting/main transaction${coverageAfter.missingCount === 1 ? '' : 's'} still need raw coverage. Copy the next target below, gather raw data, and paste the next raw set here.`;
        this.refreshCaseSummary();
      } else {
        this.showRawDataImportModal = false;
        this.activateLogicalSheet('raw');
      }

      this.showCaseToast(toastMessage);
    },

    async openTool68PreparationModal() {
      return this.runCaseModalOperation({
        title: 'Opening raw-data preparation…',
        message: 'Comparing Main Data against available Raw Data.',
        target: 'tool68-preparation-modal',
        finishMessage: 'Raw-data preparation view ready.'
      }, async () => {
        this.ensureCaseWorkbookScaffold({ silent: true });
        this.tool68PreparationStatus = '';
        this.tool68PreparationConfirmReprocess = false;
        this.showTool68PreparationModal = true;
        await this.yieldToCaseUi();
        this.refreshTool68PreparationRows();
        return true;
      });
    },

    cancelTool68PreparationModal() {
      if (this.tool68PreparationRunning) return;
      this.showTool68PreparationModal = false;
    },

    buildTool68PreparationRows() {
      const analysis = buildSaplingPreparationAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw'),
        this.caseSupportState.workflowState || {}
      );

      return analysis.rows.map((row) => ({
        ...row,
        include: row.rawCovered && !row.processed
      }));
    },

    refreshTool68PreparationRows() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.tool68PreparationRows = this.buildTool68PreparationRows();
      this.tool68PreparationStatus = this.tool68PreparationStageSummary;
      return this.tool68PreparationRows;
    },

    selectUnprocessedTool68PreparationRows() {
      this.tool68PreparationRows = this.tool68PreparationRows.map(row => ({
        ...row,
        include: !!row.rawCovered && !row.processed
      }));
      this.tool68PreparationConfirmReprocess = false;
    },

    selectReprocessAvailableTool68PreparationRows() {
      this.tool68PreparationRows = this.tool68PreparationRows.map(row => ({
        ...row,
        include: !!row.rawCovered && !!row.hasNewerRawSet
      }));
    },

    clearTool68PreparationSelections() {
      this.tool68PreparationRows = this.tool68PreparationRows.map(row => ({
        ...row,
        include: false
      }));
      this.tool68PreparationConfirmReprocess = false;
    },

    buildTool68PreparationCheckResult(analysis = null) {
      const effectiveAnalysis = analysis || buildSaplingPreparationAnalysis(
        this.getMatrixForLogicalSheet('main'),
        this.getMatrixForLogicalSheet('raw'),
        this.caseSupportState.workflowState || {}
      );
      const startingReady = effectiveAnalysis.analysis.mainTransactionCount > 0 && effectiveAnalysis.analysis.trashRowCount === 0;
      let status = 'fail';
      let message = '';

      if (!startingReady) {
        status = 'warn';
        message = 'Confirm starting main transactions before checking sapling preparation.';
      } else if (effectiveAnalysis.rawMissing > 0) {
        status = 'fail';
        message = `${effectiveAnalysis.rawMissing} starting/main transaction${effectiveAnalysis.rawMissing === 1 ? '' : 's'} still need matching raw data before Tool 68 preparation can finish.`;
      } else if (effectiveAnalysis.unprocessedCount > 0) {
        status = 'fail';
        message = `${effectiveAnalysis.unprocessedCount} raw-backed starting/main transaction${effectiveAnalysis.unprocessedCount === 1 ? '' : 's'} still need Tool 68 sapling preparation.`;
      } else if (effectiveAnalysis.reprocessAvailableCount > 0) {
        status = 'fail';
        message = `${effectiveAnalysis.reprocessAvailableCount} processed starting/main transaction${effectiveAnalysis.reprocessAvailableCount === 1 ? '' : 's'} have a newer bottom-most raw set available. Reprocess or manually complete the workflow step if the existing Main Data is intentionally current.`;
      } else if (effectiveAnalysis.ready) {
        status = 'pass';
        message = `${effectiveAnalysis.processedCount} starting/main transaction${effectiveAnalysis.processedCount === 1 ? '' : 's'} appear prepared into sapling blocks. Single-hop or zero-hop flows count when Column Q says Path ends.`;
      } else {
        status = 'fail';
        message = 'Sapling preparation is not complete yet.';
      }

      return this.buildCheckResult('sapling_prepared', status, message, {
        totalMainTransactions: effectiveAnalysis.total,
        processedCount: effectiveAnalysis.processedCount,
        unprocessedCount: effectiveAnalysis.unprocessedCount,
        rawMissing: effectiveAnalysis.rawMissing,
        reprocessAvailableCount: effectiveAnalysis.reprocessAvailableCount
      });
    },

    refreshTool68PreparationCheckResult(analysis = null) {
      const result = this.buildTool68PreparationCheckResult(analysis);
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        sapling_prepared: result
      };
      this.persistCaseSupportState();
      this.refreshCaseSummary();
      return result;
    },

    runTool68PreparationForSelectedRows() {
      if (this.tool68PreparationRunning) return;

      const selectedKeys = new Set(this.tool68PreparationRows.filter(row => row.include && row.canProcess).map(row => row.key));
      if (selectedKeys.size === 0) {
        this.tool68PreparationStatus = 'Select at least one raw-backed main transaction to prepare.';
        return;
      }

      if (this.tool68PreparationNeedsReprocessConfirmation && !this.tool68PreparationConfirmReprocess) {
        this.tool68PreparationStatus = 'Confirm reprocessing before overwriting a main transaction flow that already appears prepared.';
        return;
      }

      this.ensureCaseWorkbookScaffold({ silent: true });
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet) {
        this.tool68PreparationStatus = 'Could not find the Main Data sheet.';
        return;
      }

      this.tool68PreparationRunning = true;
      this.tool68PreparationStatus = `Preparing ${selectedKeys.size} main transaction${selectedKeys.size === 1 ? '' : 's'} with Tool 68…`;

      try {
        const mainState = this.getSheetStateById(mainSheet.id);
        const mainData = normalizeCaseMatrix(mainState.data);
        const rawData = this.getMatrixForLogicalSheet('raw');
        const currentAnalysis = buildSaplingPreparationAnalysis(mainData, rawData, this.caseSupportState.workflowState || {});
        const rowsToProcess = currentAnalysis.rows.filter(row => selectedKeys.has(row.key) && row.canProcess);

        if (rowsToProcess.length === 0) {
          this.tool68PreparationStatus = 'The selected rows are no longer processable. Refresh the modal and try again.';
          return;
        }

        const replacements = [];
        const auditRows = [];
        let applied = 0;
        let failed = 0;
        let reprocessed = 0;
        let outputRows = 0;
        const updatedRecords = {
          ...(this.caseSupportState.workflowState?.tool68PreparedByMainKey || {})
        };
        const processedAtUtc = new Date().toISOString();

        rowsToProcess.forEach((row, index) => {
          const inputRows = buildTool68InputRowsForRawSet(row.rawSet);
          if (inputRows.length <= 1) {
            failed += 1;
            auditRows.push({
              ...row,
              applied: false,
              statusLabel: 'Failed',
              resultFeedback: 'No usable raw rows were found inside the matched raw set.'
            });
            return;
          }

          const chainUsed = normalizeTool68ChainMode(this.tool68PreparationChainMode, row.rawSet?.rows || row.leadRow);
          const baseLabel = firstNonEmptyValue(row.pathLabel, row.leadRow?.[0], String(index + 1));
          const result = runTool68BFlowAnalysis(inputRows, {
            chain: chainUsed,
            baseLabel,
            labelLongestPathFirst: this.tool68PreparationLabelLongestPathFirst,
            pruneBTCUnfollowed: this.tool68PreparationPruneBtcUnfollowed,
            extraSpacing: this.tool68PreparationExtraSpacing
          });

          if (!result.success) {
            failed += 1;
            auditRows.push({
              ...row,
              applied: false,
              statusLabel: 'Failed',
              chainUsed,
              resultFeedback: result.feedback || 'Tool 68 did not complete successfully.'
            });
            return;
          }

          let preparedRows = [];
          try {
            preparedRows = normalizeTool68ProcessedRowsForMainRewrite(row.block, result.outputRows, { baseLabel });
          } catch (error) {
            failed += 1;
            auditRows.push({
              ...row,
              applied: false,
              statusLabel: 'Failed',
              chainUsed,
              resultFeedback: error?.message || 'Tool 68 output could not be normalized for Main Data.'
            });
            return;
          }

          replacements.push({
            blockStartRowIndex: row.blockStartRowIndex,
            blockReplaceEndRowIndex: row.blockReplaceEndRowIndex,
            nextStartRowIndex: row.block.nextStartRowIndex,
            preparedRows
          });

          updatedRecords[row.key] = {
            mainKey: row.key,
            pathLabel: baseLabel,
            transactionHash: row.hash,
            toAddress: row.toAddress,
            rawSetSignature: row.rawSetSignature,
            rawRootRowIndex: row.rawSet?.rootRowIndex ?? null,
            rawRootRowNumber: row.rawRootRowNumber,
            rawRowCount: row.rawRowCount,
            chainUsed,
            outputRowCount: preparedRows.filter(rowHasMeaningfulData).length,
            processedAtUtc,
            status: 'prepared_and_applied'
          };

          if (row.processed) reprocessed += 1;
          applied += 1;
          outputRows += preparedRows.filter(rowHasMeaningfulData).length;
          auditRows.push({
            ...row,
            applied: true,
            statusLabel: row.processed ? 'Reprocessed' : 'Applied',
            chainUsed,
            outputRowCount: preparedRows.filter(rowHasMeaningfulData).length,
            resultFeedback: result.feedback || 'Tool 68 completed.'
          });
        });

        if (replacements.length > 0) {
          const nextData = buildMainDataWithPreparedSaplingReplacements(mainData, replacements, {
            addSpacerAfterBlock: this.tool68PreparationAddSpacerAfterBlock
          });
          this.writeSheetStateById(mainSheet.id, {
            ...mainState,
            data: nextData,
            frozenTop: 1
          });

          this.markCaseDataMutated('tool68_sapling_prepared_from_raw_data', {
            selected: rowsToProcess.length,
            applied,
            failed,
            reprocessed,
            outputRows
          });

          this.caseSupportState.workflowState = {
            ...(this.caseSupportState.workflowState || {}),
            tool68PreparedByMainKey: updatedRecords
          };
          this.persistCaseSupportState();
        }

        const analysisAfter = buildSaplingPreparationAnalysis(
          this.getMatrixForLogicalSheet('main'),
          this.getMatrixForLogicalSheet('raw'),
          this.caseSupportState.workflowState || {}
        );
        const rawCheck = this.buildRawDataCoverageCheckResult(analysisAfter.coverage);
        const saplingCheck = this.refreshTool68PreparationCheckResult(analysisAfter);
        this.caseSupportState.checkResultsById = {
          ...(this.caseSupportState.checkResultsById || {}),
          raw_data: rawCheck,
          sapling_prepared: saplingCheck
        };
        this.persistCaseSupportState();

        this.recordWorkflowToolRun('prepare_raw_data_with_tool_68', {
          selected: rowsToProcess.length,
          applied,
          failed,
          reprocessed,
          outputRows
        });

        this.loadOutputRows(buildTool68PreparationAuditRows(auditRows, {
          selected: rowsToProcess.length,
          applied,
          failed,
          reprocessed,
          outputRows
        }), {
          title: 'Tool 68 sapling preparation audit',
          detail: 'Audit only. Successful Tool 68 results were written directly into Main Data by replacing the selected main transaction block.',
          sourceActionId: 'prepare_raw_data_with_tool_68',
          target: 'audit-only'
        });

        if (saplingCheck.status === 'pass') {
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_TOOL_68_MAIN_DATA_PREPARED, 'prepare_raw_data_with_tool_68', { silent: true });
        }

        this.tool68PreparationLastRunSummary = {
          selected: rowsToProcess.length,
          applied,
          failed,
          reprocessed,
          outputRows,
          complete: saplingCheck.status === 'pass'
        };

        const failedDetails = auditRows
          .filter(row => !row.applied)
          .map((row) => {
            const rowNumber = row.spreadsheetRowNumber ? `Main row ${row.spreadsheetRowNumber}` : 'Selected row';
            const chainText = row.chainUsed ? ` (${row.chainUsed})` : '';
            const message = normalizeCell(row.resultFeedback || row.message) || 'No detailed failure message was returned.';
            return `${rowNumber}${chainText}: ${message}`;
          });
        const finalStatus = applied > 0
          ? [
            `Prepared ${applied} main transaction${applied === 1 ? '' : 's'} into Main Data.`,
            failed > 0 ? `${failed} selected item${failed === 1 ? '' : 's'} failed and need review.` : '',
            ...failedDetails.slice(0, 3)
          ].filter(Boolean).join('\n')
          : [
            `No Tool 68 rows were applied. ${failed} selected item${failed === 1 ? '' : 's'} failed.`,
            ...failedDetails.slice(0, 5)
          ].filter(Boolean).join('\n');

        this.refreshTool68PreparationRows();
        this.tool68PreparationStatus = finalStatus || this.tool68PreparationStageSummary;
        this.activateLogicalSheet('main');
        this.showCaseToast(applied > 0
          ? `Prepared ${applied} main transaction${applied === 1 ? '' : 's'} into Main Data. ${failed > 0 ? `${failed} failed and need review.` : ''}`.trim()
          : `No Tool 68 rows were applied. ${failed} selected item${failed === 1 ? '' : 's'} failed. See the modal status or Tool 68 audit for details.`);
      } finally {
        this.tool68PreparationRunning = false;
      }
    },

    refreshMainPathLabelRows() {
      const existingEdits = new Map((this.mainPathLabelRows || []).map(row => [row.id, row.proposedLabel]));
      this.mainPathLabelRows = this.buildMainPathLabelRows().map((row) => ({
        ...row,
        proposedLabel: existingEdits.get(row.id) || row.proposedLabel
      }));
      this.refreshMainPathLabelRowStatuses();
      return this.mainPathLabelRows;
    },

    resetMainPathLabelProposals() {
      this.mainPathLabelRows = this.mainPathLabelRows.map((row, index) => ({
        ...row,
        proposedLabel: String(index + 1)
      }));
      this.refreshMainPathLabelRowStatuses();
      this.mainPathLabelStatus = 'Numeric label proposals reset to 1, 2, 3 in UTC chronological order.';
    },

    refreshMainPathLabelRowStatuses() {
      const labelCounts = new Map();
      this.mainPathLabelRows.forEach((row) => {
        const normalized = normalizeNumericMainPathLabel(row.proposedLabel);
        if (normalized) labelCounts.set(normalized, (labelCounts.get(normalized) || 0) + 1);
      });

      this.mainPathLabelRows = this.mainPathLabelRows.map((row) => {
        const proposed = normalizeNumericMainPathLabel(row.proposedLabel);
        const current = normalizeNumericMainPathLabel(row.currentLabel);
        const hasDate = !!row.dateInfo?.isValid;
        const duplicate = proposed && (labelCounts.get(proposed) || 0) > 1;
        const continuationCount = Array.isArray(row.continuationLabelRows) ? row.continuationLabelRows.length : 0;
        let status = 'ready';
        let statusLabel = 'Ready';
        let message = '';

        if (!row.hash) {
          status = 'blocked';
          statusLabel = 'Missing hash';
          message = 'Column B is required.';
        } else if (!hasDate) {
          status = 'blocked';
          statusLabel = 'Needs UTC date';
          message = 'Complete Column E before assigning chronological numeric labels.';
        } else if (!proposed) {
          status = 'attention';
          statusLabel = 'Invalid proposal';
          message = 'Use a positive whole number, such as 1, 2, or 3.';
        } else if (duplicate) {
          status = 'attention';
          statusLabel = 'Duplicate label';
          message = 'Each main transaction needs a unique numeric label.';
        } else if (proposed !== String(row.order)) {
          status = 'attention';
          statusLabel = 'Out of sequence';
          message = `Expected ${row.order} for this UTC chronological position.`;
        } else if (current === proposed && continuationCount === 0) {
          status = 'unchanged';
          statusLabel = 'Already correct';
          message = 'Column A already matches the proposed numeric label.';
        } else {
          status = 'ready';
          statusLabel = continuationCount > 0 ? 'Ready · cleanup' : 'Ready';
          message = continuationCount > 0
            ? `${continuationCount} continuation row${continuationCount === 1 ? '' : 's'} will have Column A cleared.`
            : (row.currentLabel ? 'Existing label will be replaced.' : 'New label will be written.');
        }

        const willChange = status === 'ready' || (status === 'unchanged' && continuationCount > 0);
        return {
          ...row,
          proposedLabel: proposed || normalizeCell(row.proposedLabel),
          status,
          statusLabel,
          message,
          willChange: willChange && (row.currentLabel !== proposed || continuationCount > 0)
        };
      });
    },

    applyMainPathLabels() {
      if (this.mainPathLabelAssignRunning) return;
      this.refreshMainPathLabelRowStatuses();

      const blocked = this.mainPathLabelRows.filter(row => row.status === 'attention' || row.status === 'blocked');
      if (blocked.length > 0) {
        this.mainPathLabelStatus = `${blocked.length} staged main transaction${blocked.length === 1 ? '' : 's'} need review before labels can be applied.`;
        return;
      }

      const rowsToApply = this.mainPathLabelRows.filter(row => row.status === 'ready' || row.status === 'unchanged');
      const auditRowsBeforeApply = rowsToApply.map(row => ({
        ...row,
        entryRows: Array.isArray(row.entryRows) ? row.entryRows.slice() : []
      }));
      if (rowsToApply.length === 0) {
        this.mainPathLabelStatus = 'No ready main transaction labels are staged.';
        return;
      }

      this.mainPathLabelAssignRunning = true;
      try {
        this.ensureCaseWorkbookScaffold({ silent: true });
        const mainSheet = this.findLogicalSheetMeta('main');
        if (!mainSheet) {
          this.mainPathLabelStatus = 'Could not find the Main Data sheet.';
          return;
        }

        const state = this.getSheetStateById(mainSheet.id);
        const data = normalizeCaseMatrix(state.data);
        let changed = false;
        let changedLabels = 0;
        let continuationCleared = 0;

        rowsToApply.forEach((draft) => {
          const rowIndex = draft.startRow;
          while (data.length <= rowIndex) {
            data.push(Array(CASE_DEFAULT_COLS).fill(''));
          }
          const leadRow = Array.isArray(data[rowIndex]) ? data[rowIndex] : Array(CASE_DEFAULT_COLS).fill('');
          data[rowIndex] = leadRow.length >= CASE_DEFAULT_COLS ? leadRow : [...leadRow, ...Array(CASE_DEFAULT_COLS - leadRow.length).fill('')];
          const proposed = normalizeNumericMainPathLabel(draft.proposedLabel);

          if (proposed && normalizeCell(data[rowIndex][0]) !== proposed) {
            data[rowIndex][0] = proposed;
            changed = true;
            changedLabels += 1;
          }

          if (this.mainPathLabelClearContinuationRows && Array.isArray(draft.entryRows)) {
            draft.entryRows.slice(1).forEach((entryRow) => {
              const continuationIndex = entryRow.rowIndex;
              if (!Number.isInteger(continuationIndex) || continuationIndex < 0) return;
              while (data.length <= continuationIndex) {
                data.push(Array(CASE_DEFAULT_COLS).fill(''));
              }
              const row = Array.isArray(data[continuationIndex]) ? data[continuationIndex] : Array(CASE_DEFAULT_COLS).fill('');
              data[continuationIndex] = row.length >= CASE_DEFAULT_COLS ? row : [...row, ...Array(CASE_DEFAULT_COLS - row.length).fill('')];
              if (normalizeCell(data[continuationIndex][0])) {
                data[continuationIndex][0] = '';
                changed = true;
                continuationCleared += 1;
              }
            });
          }
        });

        if (changed) {
          this.writeSheetStateById(mainSheet.id, {
            ...state,
            data,
            frozenTop: 1
          });

          this.markCaseDataMutated('main_transaction_path_labels_assigned', {
            labelsApplied: rowsToApply.length,
            changedLabels,
            continuationCleared
          });

          if (this.autoSaveEnabled) {
            this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
          }
        }

        this.refreshMainPathLabelRows();
        const checkState = this.refreshMainPathLabelCheckResult();
        const summary = {
          total: rowsToApply.length,
          applied: rowsToApply.length,
          changed: changedLabels,
          continuationCleared,
          checkPass: checkState.pathLabelsPass
        };
        this.mainPathLabelLastRunSummary = summary;

        this.recordWorkflowToolRun('apply_main_path_labels_numeric_auto', summary);
        this.loadOutputRows(buildMainPathLabelAuditRows(auditRowsBeforeApply, summary), {
          title: 'Main transaction path label audit',
          detail: 'Audit only. Standard numeric main path labels were written directly to Main Data Column A for top-level entries.',
          sourceActionId: 'apply_main_path_labels_numeric_auto',
          target: 'audit-only'
        });

        if (checkState.pathLabelsPass) {
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_MAIN_TRANSACTION_PATH_LABELS_ASSIGNED, 'apply_main_path_labels_numeric_auto', { silent: true });
        }

        this.activateLogicalSheet('main');
        this.mainPathLabelStatus = changed
          ? `Assigned numeric main path labels for ${rowsToApply.length} top-level main transaction${rowsToApply.length === 1 ? '' : 's'}.`
          : 'Main transaction path labels already matched the standard numeric sequence.';
        this.showCaseToast(this.mainPathLabelStatus);
      } finally {
        this.mainPathLabelAssignRunning = false;
      }
    },

    buildMainTransactionNetworkLookupRows() {
      const mainData = this.getMatrixForLogicalSheet('main');
      const analysis = analyzeStartingMainTransactions(mainData);
      const entries = parseCaseEntries(mainData);
      const entryByStartRow = new Map(entries.map(entry => [entry.startRow, entry]));

      return analysis.mainTransactionRows.map(({ rowIndex, row }, index) => {
        const entry = entryByStartRow.get(rowIndex) || {
          startRow: rowIndex,
          hash: normalizeCell(row?.[1]),
          rows: [{ rowIndex, row }]
        };
        const entryRows = Array.isArray(entry.rows) && entry.rows.length > 0 ? entry.rows : [{ rowIndex, row }];
        const hash = normalizeCell(row?.[1] || entry.hash);
        const pathLabel = normalizeCell(row?.[0]);
        const chainId = firstNonEmptyEntryValue(entryRows, 19) || normalizeCell(row?.[19]);
        const chainName = firstNonEmptyEntryValue(entryRows, 20) || normalizeCell(row?.[20]);
        const toAddressHint = normalizeCell(row?.[3]) || firstNonEmptyEntryValue(entryRows, 3);
        const fromAddressHint = normalizeCell(row?.[2]) || firstNonEmptyEntryValue(entryRows, 2);
        const needsNetwork = !!hash && (!chainId || !chainName);
        const currentNetworkLabel = chainName && chainId
          ? `${chainName} (${chainId})`
          : chainName || chainId || 'Missing';

        return {
          id: `network-main-${rowIndex}-${index}-${normalizeComparisonKey(hash) || 'blank'}`,
          rowIndex,
          spreadsheetRowNumber: rowIndex + 1,
          entryRowIndexes: entryRows.map(entryRow => entryRow.rowIndex).filter(entryIndex => Number.isInteger(entryIndex) && entryIndex >= 0),
          pathLabel,
          hash,
          toAddressHint,
          fromAddressHint,
          chainId,
          chainName,
          currentNetworkLabel,
          needsNetwork,
          include: needsNetwork,
          status: needsNetwork ? 'pending' : 'complete',
          statusLabel: needsNetwork ? 'Needs lookup' : 'Already filled',
          message: needsNetwork ? '' : 'Column T/U already present.',
          source: '',
          tried: [],
          lookupResult: null
        };
      });
    },

    refreshNetworkLookupRows() {
      this.ensureCaseWorkbookScaffold({ silent: true });
      this.networkLookupRows = this.buildMainTransactionNetworkLookupRows();
      this.networkLookupStatus = this.networkLookupStageSummary;
      return this.networkLookupRows;
    },

    selectPendingNetworkLookupRows() {
      this.networkLookupRows = this.networkLookupRows.map(row => ({
        ...row,
        include: !!row.needsNetwork && !!row.hash
      }));
    },

    clearNetworkLookupSelections() {
      this.networkLookupRows = this.networkLookupRows.map(row => ({
        ...row,
        include: false
      }));
    },

    updateNetworkLookupRow(rowId, patch = {}) {
      this.networkLookupRows = this.networkLookupRows.map(row => row.id === rowId ? { ...row, ...patch } : row);
    },

    async getCaseBuddyApiBearerToken() {
      const sessionToken = extractAuthTokenCandidate(this.authSession) || extractAuthTokenCandidate(this.authSessionRaw);
      if (sessionToken) return sessionToken;

      const serviceMethodNames = ['getToken', 'getAccessToken', 'getAuthToken', 'getJwtToken', 'getIdToken'];
      for (const methodName of serviceMethodNames) {
        if (typeof AuthService?.[methodName] !== 'function') continue;
        try {
          const token = extractAuthTokenCandidate(await AuthService[methodName]());
          if (token) return token;
        } catch (error) {
          console.warn(`AuthService.${methodName} did not return a usable token.`, error);
        }
      }

      if (typeof AuthService?.getSession === 'function') {
        try {
          const session = await AuthService.getSession();
          this.authSessionRaw = session;
          this.authSession = normalizeAuthSession(session);
          const token = extractAuthTokenCandidate(session) || extractAuthTokenCandidate(this.authSession);
          if (token) return token;
        } catch (error) {
          console.warn('AuthService.getSession did not return a usable token.', error);
        }
      }

      return collectStoredAuthToken();
    },

    async postCaseBuddyApiJson(endpoint, payload = {}) {
      const bearerToken = await this.getCaseBuddyApiBearerToken();
      if (!bearerToken) {
        throw new Error('No API bearer token is available. Sign in again before using Case Buddy API tools.');
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearerToken}`
        },
        credentials: 'include',
        body: JSON.stringify(payload || {})
      });

      const rawText = await response.text();
      let data = null;
      try {
        data = rawText ? JSON.parse(rawText) : null;
      } catch {
        throw new Error(`Case Buddy API returned non-JSON response: ${rawText.slice(0, 160)}`);
      }

      if (!response.ok) {
        throw new Error(data?.message || `Case Buddy API request failed with HTTP ${response.status}.`);
      }

      if (!data || data.status === 'error') {
        throw new Error(data?.message || 'Case Buddy API request failed.');
      }

      return data;
    },

    lookupTransactionNetwork(row) {
      return this.postCaseBuddyApiJson(CASE_BUDDY_NETWORK_LOOKUP_ENDPOINT, {
        txHash: row.hash,
        toAddressHint: row.toAddressHint || '',
        fromAddressHint: row.fromAddressHint || '',
        fillDateIfMissing: !!this.networkLookupFillDateIfMissing
      });
    },

    applyNetworkLookupResultsToMainSheet(rowsToApply = []) {
      const mainSheet = this.findLogicalSheetMeta('main');
      if (!mainSheet || !Array.isArray(rowsToApply) || rowsToApply.length === 0) {
        return { changed: false, rowsUpdated: 0, dateRowsFilled: 0, normalizedHashCount: 0 };
      }

      const state = this.getSheetStateById(mainSheet.id);
      const data = normalizeCaseMatrix(state.data);
      let changed = false;
      let rowsUpdated = 0;
      let dateRowsFilled = 0;
      let normalizedHashCount = 0;

      rowsToApply.forEach((draft) => {
        const result = draft.lookupResult || {};
        const chainId = normalizeCell(result.caseChainId);
        const chainName = normalizeCell(result.caseChainName);
        const normalizedTxHash = normalizeCell(result.normalizedTxHash);
        const utcDate = formatNetworkLookupUtcDate(result);
        const entryRowIndexes = Array.isArray(draft.entryRowIndexes) && draft.entryRowIndexes.length > 0
          ? draft.entryRowIndexes
          : [draft.rowIndex];
        let entryChanged = false;

        const maxRowIndex = Math.max(...entryRowIndexes, draft.rowIndex);
        while (data.length <= maxRowIndex) {
          data.push(Array(CASE_DEFAULT_COLS).fill(''));
        }

        entryRowIndexes.forEach((rowIndex) => {
          const row = Array.isArray(data[rowIndex]) ? data[rowIndex] : Array(CASE_DEFAULT_COLS).fill('');
          data[rowIndex] = row.length >= CASE_DEFAULT_COLS ? row : [...row, ...Array(CASE_DEFAULT_COLS - row.length).fill('')];

          if (chainId && normalizeCell(data[rowIndex][19]) !== chainId) {
            data[rowIndex][19] = chainId;
            changed = true;
            entryChanged = true;
          }

          if (chainName && normalizeCell(data[rowIndex][20]) !== chainName) {
            data[rowIndex][20] = chainName;
            changed = true;
            entryChanged = true;
          }

          if (this.networkLookupFillDateIfMissing && utcDate && !normalizeCell(data[rowIndex][4])) {
            data[rowIndex][4] = utcDate;
            changed = true;
            entryChanged = true;
            dateRowsFilled += 1;
          }
        });

        if (normalizedTxHash && normalizeComparisonKey(normalizedTxHash) !== normalizeComparisonKey(data[draft.rowIndex]?.[1])) {
          data[draft.rowIndex][1] = normalizedTxHash;
          changed = true;
          entryChanged = true;
          normalizedHashCount += 1;
        }

        if (entryChanged) rowsUpdated += 1;
      });

      if (!changed) {
        return { changed: false, rowsUpdated, dateRowsFilled, normalizedHashCount };
      }

      this.writeSheetStateById(mainSheet.id, {
        ...state,
        data,
        frozenTop: 1
      });

      this.markCaseDataMutated('transaction_networks_identified', {
        rowsUpdated,
        dateRowsFilled,
        normalizedHashCount
      });

      if (this.autoSaveEnabled) {
        this.saveCurrentProjectFile({ persistInput: true, persistOutput: false, markSavedAt: true });
      }

      return { changed: true, rowsUpdated, dateRowsFilled, normalizedHashCount };
    },

    refreshNetworkDiscoveryCheckResults() {
      const summary = this.refreshCaseSummary();
      const mainData = this.getMatrixForLogicalSheet('main');
      const analysis = analyzeStartingMainTransactions(mainData);
      const startingMainTransactionsReady = analysis.mainTransactionCount > 0 && analysis.trashRowCount === 0;
      this.caseSupportState.checkResultsById = {
        ...(this.caseSupportState.checkResultsById || {}),
        main_transactions: this.buildCheckResult(
          'main_transactions',
          startingMainTransactionsReady ? 'pass' : 'fail',
          analysis.mainTransactionCount > 0
            ? analysis.trashRowCount === 0
              ? `${analysis.mainTransactionCount} starting main transaction${analysis.mainTransactionCount === 1 ? '' : 's'} detected (${analysis.mode}).`
              : `${analysis.mainTransactionCount} starting main transaction${analysis.mainTransactionCount === 1 ? '' : 's'} detected, but ${analysis.trashRowCount} row${analysis.trashRowCount === 1 ? '' : 's'} have Column D without Column B in the starting-data area.`
            : 'No starting main transactions detected. Add rows with transaction hashes in Column B, with optional To Addresses in Column D.'
        ),
        networks: this.buildCheckResult(
          'networks',
          !startingMainTransactionsReady ? 'warn' : summary.missingNetworks === 0 ? 'pass' : 'fail',
          !startingMainTransactionsReady ? 'Confirm starting main transactions before checking networks.' : summary.missingNetworks === 0 ? 'Starting/main transaction rows have Chain Id and Chain Name. Subtransaction rows are not checked at this stage.' : `${summary.missingNetworks} starting/main transaction row${summary.missingNetworks === 1 ? '' : 's'} missing Chain Id or Chain Name. Subtransaction rows are not checked at this stage.`
        )
      };
      this.persistCaseSupportState();
      return summary;
    },

    recordWorkflowToolRun(actionId, details = {}) {
      const currentRuns = Array.isArray(this.caseSupportState.workflowState?.toolRuns)
        ? this.caseSupportState.workflowState.toolRuns
        : [];

      this.caseSupportState.workflowState = {
        ...(this.caseSupportState.workflowState || {}),
        toolRuns: [
          ...currentRuns,
          {
            actionId,
            details,
            ranAtUtc: new Date().toISOString(),
            revision: Number(this.caseSupportState.caseDataRevision) || 0
          }
        ].slice(-80)
      };
      this.persistCaseSupportState();
    },

    async runNetworkDiscoveryLookup() {
      if (this.networkLookupRunning) return;

      if (!this.authSessionLoaded) {
        await this.loadAuthSession();
      }

      if (!this.authSession.isAuthenticated) {
        this.networkLookupStatus = 'Please sign in before using Case Buddy backend lookup tools.';
        this.showCaseToast(this.networkLookupStatus);
        return;
      }

      const selectedRows = this.networkLookupRows.filter(row => row.include && row.needsNetwork && row.hash);
      if (selectedRows.length === 0) {
        this.networkLookupStatus = 'No missing-network main transaction rows are selected.';
        return;
      }

      const token = await this.getCaseBuddyApiBearerToken();
      if (!token) {
        this.networkLookupStatus = 'Your login session is loaded, but no bearer token was found for the API request.';
        this.showCaseToast(this.networkLookupStatus);
        return;
      }

      this.networkLookupRunning = true;
      this.networkLookupStatus = `Looking up networks for ${selectedRows.length} transaction${selectedRows.length === 1 ? '' : 's'}…`;
      let resolved = 0;
      let unresolved = 0;
      let normalizedHashCount = 0;
      const resolvedDrafts = [];

      try {
        for (let index = 0; index < selectedRows.length; index += 1) {
          const row = selectedRows[index];
          this.updateNetworkLookupRow(row.id, {
            status: 'running',
            statusLabel: `Lookup ${index + 1}/${selectedRows.length}`,
            message: '',
            lookupResult: null
          });
          this.networkLookupStatus = `Looking up ${index + 1}/${selectedRows.length}: ${abbreviateCaseBuddyHash(row.hash)}`;

          try {
            const payload = await this.lookupTransactionNetwork(row);
            if (payload.status === 'success' && payload.result?.found) {
              const result = payload.result;
              resolved += 1;
              if (result.normalizedTxHash && normalizeComparisonKey(result.normalizedTxHash) !== normalizeComparisonKey(row.hash)) {
                normalizedHashCount += 1;
              }
              const chainId = normalizeCell(result.caseChainId);
              const chainName = normalizeCell(result.caseChainName);
              const updatedDraft = {
                ...row,
                lookupResult: result,
                chainId,
                chainName,
                currentNetworkLabel: `${chainName || 'Unknown chain'}${chainId ? ` (${chainId})` : ''}`,
                needsNetwork: false,
                include: false,
                status: 'resolved',
                statusLabel: chainName || 'Resolved',
                source: normalizeCell(result.source),
                tried: Array.isArray(payload.tried) ? payload.tried : [],
                message: result.inputWasNormalized ? 'Hash normalized before applying.' : 'Network confirmed.'
              };
              resolvedDrafts.push(updatedDraft);
              this.updateNetworkLookupRow(row.id, updatedDraft);
            } else {
              unresolved += 1;
              this.updateNetworkLookupRow(row.id, {
                status: 'unresolved',
                statusLabel: 'Not found',
                message: payload?.message || 'No supported network confirmed this transaction.',
                tried: Array.isArray(payload?.tried) ? payload.tried : [],
                lookupResult: payload?.result || null
              });
            }
          } catch (error) {
            unresolved += 1;
            this.updateNetworkLookupRow(row.id, {
              status: 'error',
              statusLabel: 'Error',
              message: error?.message || 'Lookup failed.',
              lookupResult: null
            });
          }

          if (index < selectedRows.length - 1) {
            await sleep(CASE_BUDDY_NETWORK_LOOKUP_DELAY_MS);
          }
        }

        const applyStats = this.applyNetworkLookupResultsToMainSheet(resolvedDrafts);
        const summary = this.refreshNetworkDiscoveryCheckResults();
        this.recordWorkflowToolRun('lookup_networks_auto', {
          requested: selectedRows.length,
          resolved,
          unresolved,
          normalizedHashCount,
          ...applyStats
        });

        this.loadOutputRows(buildNetworkLookupAuditRows(this.networkLookupRows, {
          requested: selectedRows.length,
          resolved,
          unresolved,
          normalizedHashCount
        }), {
          title: 'Network lookup audit',
          detail: 'Audit only. Successful network lookup values were written directly to Main Data. Do not append this audit to case rows.',
          sourceActionId: 'lookup_networks_auto',
          target: 'audit-only'
        });

        if (summary.missingNetworks === 0) {
          this.recordWorkflowSignal(WORKFLOW_SIGNAL_TRANSACTION_NETWORKS_IDENTIFIED, 'lookup_networks_auto', { silent: true });
        }

        this.networkLookupLastRunSummary = {
          requested: selectedRows.length,
          resolved,
          unresolved,
          normalizedHashCount,
          ...applyStats
        };
        this.networkLookupStatus = `Resolved ${resolved} of ${selectedRows.length} transaction${selectedRows.length === 1 ? '' : 's'}. ${unresolved > 0 ? `${unresolved} still need manual review.` : 'All selected rows were updated.'}`;
        this.showCaseToast(this.networkLookupStatus);
      } finally {
        this.networkLookupRunning = false;
      }
    }
};
